import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, ComposedChart,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, ReferenceLine, LabelList,
} from "recharts";
import axios from "axios";
import { apiGet, apiPost, apiPut, API } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, KpiRow, Card, SectionHeading, SubTabs, DataTable, StatusBadge, tier } from "../components/dc";
import { Button } from "../components/ui/button";
import { RefreshCw, MapPin, Loader2, Upload, Bell, Tv, Download } from "lucide-react";

// Palette (matches the app design tokens).
const PINK = "#ec4899", PURPLE = "#a855f7", CYAN = "#67e0f0";
const GREEN = "#34d399", AMBER = "#fbbf24", RED = "#fb7185", MUTE = "#9d8fc0";
const STATUS_COLORS = {
  Completed: GREEN, Processing: CYAN, "Awaiting Supplier": PURPLE,
  "Submission Failed": RED, "Fulfillment Declined": AMBER, Withdrawn: MUTE, Other: "#5b4b7a",
};
// Display-only relabels — backend keeps the raw status keys. "Fulfillment
// Declined" reads as a system failure but is really supplier/engineer-cancelled
// orders, so we show it as "Order Cancelled" (per the OSS standup).
const STATUS_LABELS = { "Fulfillment Declined": "Order Cancelled" };
const statusLabel = (s) => STATUS_LABELS[s] || s;
const SUPPLIER_COLORS = [PINK, PURPLE, CYAN, GREEN, AMBER, RED];

const AXIS = { fontSize: 11, fill: MUTE };
const tooltipStyle = { background: "#17102b", border: "1px solid #2a1c47", borderRadius: 8, color: "#f5f2fb", fontSize: 12 };
// recharts renders item/label text in the series colour by default (often too dark
// on our dark tooltip) — force readable colours.
const TT_ITEM = { color: "#f5f2fb", fontSize: 12 };
const TT_LABEL = { color: "#cabde6", fontWeight: 600, marginBottom: 2 };

const chartCard = (title, node, subtitle) => (
  <Card>
    <SectionHeading title={title} subtitle={subtitle} />
    <div style={{ width: "100%", height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">{node}</ResponsiveContainer>
    </div>
  </Card>
);

// ── Order geography: UK choropleth (D3 + datamaps GBR topology) ──────────────
// ── D3 loader + region lookup for the choropleth ─────────────────────────────
function useD3() {
  const [ready, setReady] = useState(typeof window !== "undefined" && !!(window.d3 && window.topojson));
  useEffect(() => {
    if (window.d3 && window.topojson) { setReady(true); return; }
    const load = (src, has) => new Promise((res, rej) => {
      if (has()) return res();
      const s = document.createElement("script");
      s.src = src; s.onload = res; s.onerror = rej; document.body.appendChild(s);
    });
    (async () => {
      try {
        await load("https://unpkg.com/d3@7/dist/d3.min.js", () => window.d3);
        await load("https://unpkg.com/topojson-client@3/dist/topojson-client.min.js", () => window.topojson);
        setReady(true);
      } catch { setReady("error"); }
    })();
  }, []);
  return ready;
}

// Topology feature (local-authority) → NUTS1 region / country, for the Region
// choropleth. Keys are the exact datamaps GBR feature names.
const _REGION_GROUPS = {
  "North East": ["Darlington", "Durham", "Hartlepool", "Middlesbrough", "Redcar and Cleveland", "Stockton-on-Tees", "Northumberland", "Newcastle upon Tyne", "North Tyneside", "South Tyneside", "Sunderland", "Gateshead"],
  "North West": ["Cumbria", "Lancashire", "Blackburn with Darwen", "Blackpool", "Halton", "Warrington", "Cheshire East", "Cheshire West and Chester", "Knowsley", "Sefton", "Liverpool", "Merseyside", "Salford", "Wigan", "Bolton", "Bury", "Rochdale", "Oldham", "Tameside", "Stockport", "Manchester", "Trafford"],
  "Yorkshire and The Humber": ["York", "East Riding of Yorkshire", "Kingston upon Hull", "North East Lincolnshire", "North Lincolnshire", "North Yorkshire", "Kirklees", "Calderdale", "Bradford", "Leeds", "Wakefield", "Rotherham", "Sheffield", "Barnsley", "Doncaster"],
  "East Midlands": ["Derby", "Derbyshire", "Leicester", "Leicestershire", "Rutland", "Nottingham", "Nottinghamshire", "Lincolnshire", "Northamptonshire"],
  "West Midlands": ["Herefordshire", "Worcestershire", "Staffordshire", "Stoke-on-Trent", "Shropshire", "Telford and Wrekin", "Warwickshire", "Birmingham", "Sandwell", "Dudley", "Wolverhampton", "Walsall", "Solihull", "Coventry"],
  "East of England": ["Cambridgeshire", "Peterborough", "Essex", "Suffolk", "Norfolk", "Thurrock", "Southend-on-Sea", "Hertfordshire", "Bedford", "Central Bedfordshire", "Luton"],
  "London": ["Barking and Dagenham", "Bexley", "Brent", "Bromley", "Camden", "Croydon", "Ealing", "Enfield", "Greenwich", "Hammersmith and Fulham", "Hounslow", "Islington", "Kensington and Chelsea", "Merton", "Redbridge", "Richmond upon Thames", "Sutton", "Tower Hamlets", "Waltham Forest", "Wandsworth", "Westminster", "Hackney", "Haringey", "Harrow", "Lambeth", "Lewisham", "Newham", "Southwark", "Havering", "Hillingdon", "Kingston upon Thames", "Barnet", "City"],
  "South East": ["Hampshire", "Southampton", "Portsmouth", "Isle of Wight", "Milton Keynes", "Buckinghamshire", "Oxfordshire", "Kent", "Medway", "Surrey", "East Sussex", "West Sussex", "Brighton and Hove", "Reading", "West Berkshire", "Wokingham", "Bracknell Forest", "Royal Borough of Windsor and Maidenhead", "Slough"],
  "South West": ["Gloucestershire", "South Gloucestershire", "Bath and North East Somerset", "Bristol", "North Somerset", "Somerset", "Devon", "Plymouth", "Torbay", "Cornwall", "Isles of Scilly", "Bournemouth", "Dorset", "Poole", "Wiltshire", "Swindon"],
  "Wales": ["Carmarthenshire", "Ceredigion", "Pembrokeshire", "Powys", "Bridgend", "Caerphilly", "Merthyr Tydfil", "Rhondda, Cynon, Taff", "Cardiff", "Vale of Glamorgan", "Neath Port Talbot", "Swansea", "Denbighshire", "Flintshire", "Wrexham", "Anglesey", "Conwy", "Gwynedd", "Blaenau Gwent", "Monmouthshire", "Newport", "Torfaen"],
  "Scotland": ["East Ayrshire", "East Dunbartonshire", "East Renfrewshire", "Glasgow", "Inverclyde", "North Ayshire", "North Lanarkshire", "Renfrewshire", "South Ayrshire", "South Lanarkshire", "West Dunbartonshire", "Aberdeen", "Aberdeenshire", "Moray", "Falkirk", "Stirling", "Clackmannanshire", "Perthshire and Kinross", "Angus", "Dundee", "Fife", "East Lothian", "Edinburgh", "Midlothian", "West Lothian", "Scottish Borders", "Highland", "Argyll and Bute", "Orkney", "Shetland Islands", "Eilean Siar", "Dumfries and Galloway"],
  "Northern Ireland": ["Belfast", "Derry", "Omagh", "Armagh", "Newry and Mourne", "Banbridge", "Craigavon", "Dungannon", "Lisburn", "Cookstown", "Antrim", "Magherafelt", "Ballymena", "Larne", "Carrickfergus", "Newtownabbey", "North Down", "Down", "Coleraine", "Ballymoney", "Limavady", "Castlereagh", "Strabane", "Fermanagh", "Ards", "Moyle"],
};
const AREA_REGION = {};
Object.entries(_REGION_GROUPS).forEach(([reg, names]) => names.forEach((n) => { AREA_REGION[n] = reg; }));

const CH_RAMP = ["#241a3d", "#5a2a63", "#8f3564", "#c74f7e", "#ec4899"];
const _chNorm = (s) => (s || "").toLowerCase().replace(/,\s*(city|county)\s+of$/i, "").trim();

const CH_STROKE = "rgba(255,255,255,0.32)"; // land borders — high contrast so it pops
const CH_ZERO = "#2a2140";                  // no-order areas — lighter than the card bg
// Far-flung isles excluded from the projection FIT (not from drawing) — they
// bloat the bounding box and shrink the mainland. Fitting to real geometry
// avoids the polygon-winding trap that fits the whole globe.
const CROP_ISLES = new Set(["Shetland Islands", "Orkney", "Eilean Siar"]);

function renderChoropleth(el, sources, data, level, opts) {
  opts = opts || {};
  const { topo, dno } = sources;
  const d3 = window.d3, topojson = window.topojson;
  el.innerHTML = "";
  el.style.position = "relative";
  const W = 560, H = 680;
  const svg = d3.select(el).append("svg")
    .attr("viewBox", `0 0 ${W} ${H}`)
    .attr("preserveAspectRatio", "xMidYMid meet")
    .style("width", "100%").style("height", "100%").style("display", "block");

  // Region view: the GB DNO licence-area boundaries (14 regions, served by the
  // backend) drawn as-is — no local-authority dissolve needed. County view:
  // draw the datamaps local authorities as before.
  let valueFor, drawFeatures, fitTarget, fc = null;
  if (level === "region" || level === "engineer") {
    // Both use the SAME 14 DNO licence-area polygons — "engineer" just colours
    // them from the engineer→region rollup (data.engineer) instead of the
    // postcode-geocoded order counts (data.regions).
    const regions = level === "engineer" ? (data.engineer || {}) : (data.regions || {});
    valueFor = (name) => regions[name] || 0;
    drawFeatures = (dno.features || []).map((f) => ({ ...f, properties: { name: f.properties.region } }));
    // Fit to the GB mainland corners (MultiPoint has no winding trap) —
    // Shetland (part of the North Scotland licence area) would otherwise
    // shrink the whole map. It still draws, clipping at the top edge.
    fitTarget = { type: "Feature", properties: {}, geometry: { type: "MultiPoint", coordinates: [[-8.2, 49.85], [1.8, 58.75]] } };
  } else {
    const obj = topo.objects && (topo.objects.gbr || Object.values(topo.objects)[0]);
    fc = topojson.feature(topo, obj);
    const areas = data.areas || {};
    const lut = {};
    Object.keys(areas).forEach((k) => { lut[_chNorm(k)] = areas[k]; });
    valueFor = (name) => lut[_chNorm(name)] || 0;
    drawFeatures = fc.features;
    // Far isles excluded from the projection FIT (not from drawing) — they
    // bloat the bounding box and shrink the mainland.
    fitTarget = { type: "FeatureCollection", features: fc.features.filter((f) => !CROP_ISLES.has(f.properties && f.properties.name)) };
  }
  const max = Math.max(1, ...drawFeatures.map((f) => valueFor(f.properties && f.properties.name)));
  const scale = d3.scaleQuantize().domain([0, max]).range(CH_RAMP);
  const fillFor = (d) => { const v = valueFor(d.properties && d.properties.name); return v > 0 ? scale(v) : CH_ZERO; };

  const tip = document.createElement("div");
  tip.style.cssText = "position:absolute;pointer-events:none;opacity:0;z-index:5;transform:translate(-50%,-120%);background:#17102b;border:1px solid #4a3a6a;border-radius:8px;padding:5px 10px;font-size:12px;font-weight:600;color:#f5f2fb;white-space:nowrap;transition:opacity .08s;box-shadow:0 4px 14px rgba(0,0,0,0.45);";
  el.appendChild(tip);

  const attachTip = (sel) => sel
    .on("mousemove", function (ev, d) {
      const n = (d.properties && d.properties.name) || "Area";
      const v = valueFor(n);
      const p = d3.pointer(ev, el);
      tip.style.left = p[0] + "px"; tip.style.top = p[1] + "px"; tip.style.opacity = "1";
      tip.textContent = opts.unit === "pct" ? `${n} · ${v}% on OSS` : `${n} · ${v} order${v === 1 ? "" : "s"}`;
      d3.select(this).attr("stroke", "#ffffff").attr("stroke-width", 1.4).raise();
    })
    .on("mouseleave", function () {
      tip.style.opacity = "0";
      d3.select(this).attr("stroke", CH_STROKE).attr("stroke-width", 0.6);
    });

  const mainPath = d3.geoPath(d3.geoMercator().fitExtent([[8, 8], [W - 8, H - 8]], fitTarget));
  const g = svg.append("g");
  attachTip(g.selectAll("path").data(drawFeatures).join("path")
    .attr("d", mainPath).attr("stroke", CH_STROKE).attr("stroke-width", 0.6).attr("fill", fillFor));

  // Scroll / drag to zoom + pan the main map (London inset is a separate layer, stays fixed).
  if (opts.zoom) {
    svg.call(d3.zoom().scaleExtent([1, 12]).on("zoom", (ev) => g.attr("transform", ev.transform)));
    svg.style("cursor", "grab");
  }

  // London inset — boroughs are too small to read on the main map, so break them
  // out into a boxed panel top-right (like the standard UK region maps).
  if (opts.londonInset && level === "county" && fc) {
    const london = fc.features.filter((f) => AREA_REGION[f.properties && f.properties.name] === "London");
    if (london.length) {
      const iw = 156, ih = 156, x0 = W - iw - 6, y0 = 6;
      const inset = svg.append("g");
      inset.append("rect").attr("x", x0).attr("y", y0).attr("width", iw).attr("height", ih)
        .attr("rx", 8).attr("fill", "#100b20").attr("stroke", "rgba(255,255,255,0.28)").attr("stroke-width", 1);
      inset.append("text").attr("x", x0 + 9).attr("y", y0 + 17)
        .attr("fill", "#cabde6").attr("font-size", 11).attr("font-weight", 600).text("London");
      const lPath = d3.geoPath(d3.geoMercator().fitExtent([[x0 + 9, y0 + 24], [x0 + iw - 9, y0 + ih - 9]], { type: "FeatureCollection", features: london }));
      attachTip(inset.selectAll("path").data(london).join("path")
        .attr("d", lPath).attr("stroke", CH_STROKE).attr("stroke-width", 0.5).attr("fill", fillFor));
    }
  }
}

function ChoroplethMap({ level, metric = "oss", height = 560, londonInset = false, zoom = false, sidePanel = false }) {
  const ready = useD3();
  const elRef = useRef(null);
  const [data, setData] = useState(null);
  const [topo, setTopo] = useState(null);
  const [dno, setDno] = useState(null);
  const [regionsRaw, setRegionsRaw] = useState(null); // /regions rows for engineer mode
  const [err, setErr] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, t, g, r] = await Promise.all([
          apiGet("/supply-scanner/choropleth"),
          apiGet("/supply-scanner/uk-topojson", { timeout: 30000 }),
          apiGet("/supply-scanner/uk-regions-geojson", { timeout: 30000 }),
          apiGet("/supply-scanner/regions").catch(() => null),
        ]);
        setData(c || { areas: {}, regions: {} });
        if (t) setTopo(t);
        if (g) setDno(g);
        setRegionsRaw(r?.regions || []);
        if (!t && !g) setErr(true);
      } catch { setErr(true); }
    })();
  }, []);

  // Engineer-region values keyed by the 14 DNO/subregion names, per chosen metric.
  const engineerMap = {};
  (regionsRaw || []).forEach((row) => {
    if (row.region === "Unknown") return;
    engineerMap[row.region] = metric === "lpo" ? row.lpoOrders : metric === "pct" ? row.transferOrders : row.ossOrders;
  });

  useEffect(() => {
    const srcReady = level === "county" ? !!topo : !!dno;
    const engReady = level !== "engineer" || regionsRaw != null;
    if (ready !== true || !elRef.current || !window.d3 || !window.topojson || !srcReady || !engReady || !data) return;
    const payload = level === "engineer" ? { ...data, engineer: engineerMap } : data;
    try { renderChoropleth(elRef.current, { topo, dno }, payload, level, { londonInset, zoom, unit: level === "engineer" && metric === "pct" ? "pct" : "orders" }); } catch { setErr(true); }
  }, [ready, topo, dno, data, regionsRaw, level, metric, londonInset, zoom]); // eslint-disable-line

  const loading = !err && (ready !== true || !data || (level === "county" ? !topo : !dno) || (level === "engineer" && regionsRaw == null));
  const counts = level === "county" ? (data?.areas || {}) : level === "engineer" ? engineerMap : (data?.regions || {});
  const top = Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 8);
  const maxTop = top.length ? top[0][1] : 1;
  const unitLabel = level === "engineer" && metric === "pct" ? "%" : "";

  const mapBox = (
    <div ref={elRef} style={{ height, flex: sidePanel ? 1 : undefined, borderRadius: 14, overflow: "hidden", background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid rgba(255,255,255,0.16)" }} />
  );

  const panel = (
    <div style={{ width: 250, flexShrink: 0, display: "flex", flexDirection: "column", justifyContent: "center", gap: 22 }}>
      <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--color-text-4)", marginBottom: 8 }}>
          {level === "engineer" ? (metric === "lpo" ? "LPO density" : metric === "pct" ? "% on OSS" : "OSS density") : "Order density"}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-3)" }}>
          <span>fewer</span>
          <span style={{ display: "inline-flex", gap: 3 }}>
            {CH_RAMP.map((c) => <span key={c} style={{ width: 18, height: 10, borderRadius: 2, background: c }} />)}
          </span>
          <span>more</span>
        </div>
      </div>
      <div>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--color-text-4)", marginBottom: 12 }}>
          Top {level === "county" ? "areas" : "regions"}
        </div>
        {top.length === 0 && <div style={{ fontSize: 12.5, color: MUTE }}>No orders resolved yet.</div>}
        {top.map(([name, count]) => (
          <div key={name} style={{ marginBottom: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 12.5, color: "var(--color-text-2)", marginBottom: 3 }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</span>
              <span style={{ fontWeight: 700, color: "var(--color-text-1)" }}>{count}{unitLabel}</span>
            </div>
            <div style={{ height: 5, borderRadius: 999, background: "var(--color-bg-panel, #0f0a1e)", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${Math.max(4, Math.round((count / maxTop) * 100))}%`, background: "linear-gradient(90deg,#a855f7,#ec4899)", borderRadius: 999 }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {(ready === "error" || err) && (
        <div style={{ padding: 16, color: AMBER, fontSize: 13 }}>
          Map couldn't load (map library or topology unreachable). Please retry shortly.
        </div>
      )}
      {loading && !err && <div style={{ padding: 24, color: MUTE, fontSize: 13 }}>Loading map…</div>}
      {sidePanel
        ? <div style={{ display: "flex", gap: 20, alignItems: "stretch" }}>{panel}{mapBox}</div>
        : mapBox}
    </>
  );
}

// Order geography panel — Region / County choropleth toggle.
function MapPanel({ progress }) {
  const [mode, setMode] = useState("region"); // "region" | "county" | "engineer"
  const [metric, setMetric] = useState("oss"); // engineer mode: "oss" | "lpo" | "pct"
  const subtitle = mode === "region"
    ? "Order density by region (DNO licence areas — geocoded from order postcodes)"
    : mode === "county"
    ? "Order density by county / local authority"
    : "OSS vs LPO by engineer region — coloured from the engineer→region map";

  const pill = (opts, val, set, testid) => (
    <div style={{ display: "inline-flex", gap: 2, background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)", borderRadius: 999, padding: 3 }}>
      {opts.map(([k, lbl]) => (
        <button key={k} onClick={() => set(k)} data-testid={`${testid}-${k}`}
          style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "5px 14px", fontSize: 12.5, fontWeight: 600,
            background: val === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent",
            color: val === k ? "#fff" : "var(--color-text-3)" }}>{lbl}</button>
      ))}
    </div>
  );

  const toggle = (
    <div style={{ display: "inline-flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {mode === "engineer" && pill([["oss", "OSS"], ["lpo", "LPO"], ["pct", "% on OSS"]], metric, setMetric, "map-metric")}
      {pill([["region", "DNO Region"], ["county", "County"], ["engineer", "Engineer region"]], mode, setMode, "map-mode")}
    </div>
  );

  return (
    <Card>
      <SectionHeading title="Order geography" subtitle={subtitle} right={toggle} />
      {progress && (
        <div style={{ marginBottom: 16 }} data-testid="ss-refresh-progress">
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, color: "var(--color-text-2)", marginBottom: 6 }}>
            <span>{progress.message}</span>
            <span style={{ color: PINK, fontWeight: 700 }}>{progress.percent}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 999, background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)", overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${Math.max(2, progress.percent)}%`,
              background: `linear-gradient(90deg, ${PURPLE}, ${PINK})`, borderRadius: 999, transition: "width .4s ease" }} />
          </div>
        </div>
      )}
      <ChoroplethMap level={mode} metric={metric} londonInset zoom sidePanel />
      <div style={{ fontSize: 12, color: MUTE, marginTop: 12, textAlign: "center" }}>
        {mode === "engineer"
          ? "Engineer region uses the 14 Octopus subregions (same boundaries as DNO), coloured by each engineer's region from Settings → Engineer Regions. Since the Aug-31 floor."
          : "Shaded by resolved order counts — hover an area for its total, scroll to zoom. Run a Full refresh to resolve any new orders."}
      </div>
      <RegionLeagueTable />
    </Card>
  );
}

// OSS vs LPO rolled up by Octopus subregion (engineer email → region), sorted
// worst-adoption-first so area managers with the most LPO left are top of the list.
function RegionLeagueTable() {
  const [d, setD] = useState(null);
  const [region, setRegion] = useState(null); // region drill-down modal
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await apiGet("/supply-scanner/regions").catch(() => null);
      if (!cancelled) setD(res || { regions: [] });
    })();
    return () => { cancelled = true; };
  }, []);

  const rows = d?.regions || [];
  return (
    <div style={{ marginTop: 20 }}>
      <SectionHeading title="By region — OSS vs LPO"
        subtitle="Which regions are still heaviest on LPO — target those area managers first" />
      {d && !d.mapped && (
        <div style={{ fontSize: 12.5, color: AMBER, marginBottom: 10 }}>
          Region lookup unavailable — the engineer→region file isn't loaded on the server.
        </div>
      )}
      <DataTable
        perPage={15}
        searchKeys={["region"]}
        searchPlaceholder="Search region…"
        defaultSortKey="transferOrders"
        defaultSortDir="asc"
        columns={[
          { key: "region", label: "Region", width: "1.4fr" },
          { key: "ossOrders", label: "OSS orders", align: "right", width: "0.9fr",
            render: (r) => <span style={{ color: PURPLE, fontWeight: 600 }}>{r.ossOrders?.toLocaleString()}</span> },
          { key: "lpoOrders", label: "LPO orders", align: "right", width: "0.9fr",
            render: (r) => <span style={{ color: r.lpoOrders > 0 ? AMBER : MUTE, fontWeight: 600 }}>{r.lpoOrders?.toLocaleString()}</span> },
          { key: "transferOrders", label: "% on OSS", align: "right", width: "0.8fr",
            render: (r) => {
              const t = (r.ossOrders || 0) + (r.lpoOrders || 0);
              return <span style={{ color: pctColor(r.transferOrders), fontWeight: 700 }}>{t ? `${r.transferOrders}%` : "—"}</span>;
            } },
          { key: "drill", label: "Engineers", align: "right", width: "0.8fr", sortable: false,
            render: (r) => (
              <button onClick={() => setRegion(r.region)} data-testid="region-drill-btn"
                style={{ border: "1px solid var(--hairline)", background: "transparent", color: CYAN, cursor: "pointer",
                  borderRadius: 7, padding: "3px 9px", fontSize: 11.5, fontWeight: 600 }}>Who?</button>
            ) },
        ]}
        rows={rows}
      />
      <div style={{ fontSize: 12, color: MUTE, marginTop: 10 }}>
        Joined on engineer email. Orders without a mapped email fall under “Unknown”. Capped to the 31 Aug floor, like the other headline figures. Click <strong>Who?</strong> to see who's ordered and who hasn't.
      </div>
      {region && <RegionEngineersModal region={region} onClose={() => setRegion(null)} />}
    </div>
  );
}

// Region drill-down — who in a region has moved to OSS, reverted, or never ordered.
const REGION_STATUS = {
  reverted: { label: "Reverted", color: RED },
  lpo_only: { label: "LPO only", color: AMBER },
  not_ordering: { label: "Not ordering", color: MUTE },
  on_oss: { label: "On OSS", color: GREEN },
};

function RegionEngineersModal({ region, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await apiGet("/supply-scanner/region-engineers", { params: { region } }).catch(() => null);
      if (!cancelled) setD(r || { engineers: [], summary: {} });
    })();
    return () => { cancelled = true; };
  }, [region]);

  const s = d?.summary || {};
  const wk = (v) => (v ? new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "—");
  const chip = (t, n, c) => (
    <span style={{ fontSize: 12, color: c, background: "var(--color-bg-panel, #0f0a1e)", border: `1px solid ${c}44`,
      borderRadius: 999, padding: "3px 10px", fontWeight: 600 }}>{t}: {n ?? 0}</span>
  );

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="region-engineers-modal">
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(880px, 96vw)", maxHeight: "88vh", overflow: "auto",
        background: "rgba(15,10,30,0.94)", border: "1px solid rgba(96,240,248,0.2)", borderRadius: 16, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text-1)" }}>{region} — who's ordering</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
              {chip("Reverted", s.reverted, RED)}{chip("LPO only", s.lpoOnly, AMBER)}
              {chip("Not ordering", s.notOrdering, MUTE)}{chip("On OSS", s.onOss, GREEN)}
            </div>
          </div>
          <button onClick={onClose} style={{ border: "1px solid var(--hairline)", background: "transparent", color: "var(--color-text-2)",
            cursor: "pointer", borderRadius: 8, padding: "4px 12px", fontSize: 12.5, fontWeight: 600 }}>Close</button>
        </div>
        {!d ? <div style={{ color: MUTE, padding: 24 }}>Loading…</div>
          : (d.engineers || []).length === 0 ? <div style={{ color: MUTE, padding: 24 }}>No engineers mapped to this region.</div>
          : (
            <DataTable perPage={12} searchKeys={["name", "email"]} searchPlaceholder="Search engineer…"
              columns={[
                { key: "name", label: "Engineer", width: "1.6fr" },
                { key: "status", label: "Status", width: "1fr", sortValue: (r) => ({ reverted: 0, lpo_only: 1, not_ordering: 2, on_oss: 3 }[r.status]),
                  render: (r) => { const st = REGION_STATUS[r.status] || {}; return <span style={{ color: st.color, fontWeight: 700 }}>{st.label}</span>; } },
                { key: "ossOrders", label: "OSS", align: "right", width: "0.6fr", render: (r) => <span style={{ color: PURPLE, fontWeight: 600 }}>{r.ossOrders}</span> },
                { key: "lpoOrders", label: "LPO", align: "right", width: "0.6fr", render: (r) => <span style={{ color: r.lpoOrders > 0 ? AMBER : MUTE, fontWeight: 600 }}>{r.lpoOrders}</span> },
                { key: "lastOssWeek", label: "Last OSS", align: "right", width: "0.8fr", render: (r) => wk(r.lastOssWeek) },
                { key: "lastLpoWeek", label: "Last LPO", align: "right", width: "0.8fr", render: (r) => wk(r.lastLpoWeek) },
              ]}
              rows={d.engineers} />
          )}
      </div>
    </div>
  );
}

// ── Overview: scrollable summary of each section's headline metrics + 2 charts ─
function Overview({ stats, month }) {
  const [spend, setSpend] = useState(null);
  const [transfer, setTransfer] = useState(null);
  const [regions, setRegions] = useState([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [sp, tr, rg] = await Promise.all([
        apiGet("/supply-scanner/spend", { params: { ...(month ? { month } : {}), basis: "successful" } }).catch(() => null),
        apiGet("/supply-scanner/transfer").catch(() => null),
        apiGet("/supply-scanner/regions").catch(() => null),
      ]);
      if (!cancelled) { setSpend(sp || {}); setTransfer(tr || {}); setRegions(rg?.regions || []); }
    })();
    return () => { cancelled = true; };
  }, [month]);

  const c = stats.cards || {};
  const sc = spend?.cards || {};
  const tt = transfer?.totals || {};
  const signedOn = c.signedOnEngineers;
  // Geography headline metrics (from /regions, Unknown already excluded).
  const regsWithData = (regions || []).filter((r) => (r.ossOrders || 0) + (r.lpoOrders || 0) > 0);
  const topRegion = regsWithData.reduce((a, r) => (!a || r.transferOrders > a.transferOrders ? r : a), null);
  const bottomRegion = regsWithData.reduce((a, r) => (!a || r.transferOrders < a.transferOrders ? r : a), null);
  const totalReverted = (regions || []).reduce((a, r) => a + (r.reverted || 0), 0);
  const journey = [
    { name: "Signed on", value: signedOn ?? c.activeUsers },
    { name: "Active", value: c.activeEngineers ?? c.activeUsers },
    { name: "Ordering OSS", value: c.activeUsers },
  ].map((f, i) => ({ ...f, fill: [CYAN, PURPLE, GREEN][i] }));
  const statusRows = Object.entries(stats.statusCounts || {}).filter(([, v]) => v > 0)
    .map(([status, count]) => ({ status, label: statusLabel(status), count }));
  const fulfil = [{ name: "Collection", value: stats.fulfillment?.collection || 0 }, { name: "Delivery", value: stats.fulfillment?.delivery || 0 }];

  return (
    <>
      <SectionDivider title="Spend" subtitle="OSS spend only — full detail on the Spend tab" />
      <KpiRow items={[
        { label: "Total spend", value: gbp0(sc.totalSpend), valueColor: GREEN, hint: "Sum of successful order value in the selected period." },
        { label: "Total orders", value: (sc.totalOrders ?? sc.orders)?.toLocaleString(), valueColor: "var(--color-text-1)", hint: "Successful orders counted." },
        { label: "Avg order value", value: gbp2(sc.avgOrderValue), valueColor: CYAN, hint: "Total spend ÷ orders." },
        { label: "Spend this week", value: gbp0(sc.spendThisWeek), valueColor: CYAN, hint: "Order value since Monday of the current UK week." },
      ]} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 8 }}>
        {chartCard("Spend by supplier",
          <BarChart data={spend?.bySupplier || []} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="supplier" tick={AXIS} interval={0} angle={-18} textAnchor="end" height={54} />
            <YAxis tick={AXIS} tickFormatter={gbpTick} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} formatter={(v) => gbp2(v)} />
            <Bar dataKey="spend" name="Spend" radius={[4, 4, 0, 0]}>
              {(spend?.bySupplier || []).map((_, i) => <Cell key={i} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />)}
            </Bar>
          </BarChart>, "Share of spend by supplier")}
        {chartCard("Order value distribution",
          <BarChart data={spend?.distribution || []} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="bucket" tick={AXIS} /><YAxis tick={AXIS} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }}
              formatter={(v, n, p) => [`${v} orders · ${gbp0(p.payload.spend)}`, "Orders"]} />
            <Bar dataKey="count" name="Orders" fill={PURPLE} radius={[4, 4, 0, 0]} />
          </BarChart>, "How many orders fall in each value band")}
      </div>

      <SectionDivider title="Order success" />
      <KpiRow items={[
        { label: "Order completed", value: `${c.successRate}%`, valueColor: c.successRate >= 75 ? GREEN : AMBER, hint: "Completed orders as a share of completed + cancelled. Successful vs cancelled; in-process and failures excluded." },
        { label: "Completed", value: stats.statusCounts?.Completed, valueColor: GREEN },
        { label: "Order cancelled", value: stats.statusCounts?.["Fulfillment Declined"], valueColor: AMBER },
        { label: "Submission failed", value: stats.statusCounts?.["Submission Failed"], valueColor: RED },
      ]} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 8 }}>
        {chartCard("Status breakdown",
          <BarChart data={statusRows} layout="vertical" margin={{ top: 6, right: 20, left: 40, bottom: 0 }}>
            <XAxis type="number" tick={AXIS} /><YAxis type="category" dataKey="label" tick={AXIS} width={120} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
            <Bar dataKey="count" radius={[0, 5, 5, 0]}>{statusRows.map((r, i) => <Cell key={i} fill={STATUS_COLORS[r.status] || MUTE} />)}</Bar>
          </BarChart>)}
        {chartCard("Collection vs delivery",
          <PieChart>
            <Pie data={fulfil} dataKey="value" nameKey="name" innerRadius={52} outerRadius={88} paddingAngle={2}><Cell fill={CYAN} /><Cell fill={PINK} /></Pie>
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} /><Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>)}
      </div>

      <SectionDivider title="Adoption" />
      <KpiRow items={[
        { label: "Adoption", value: `${c.adoptionRateActive ?? 0}%`, valueColor: (c.adoptionRateActive ?? 0) >= 50 ? GREEN : AMBER, hint: "Of everyone ordering on either system, the share on OSS." },
        ...(c.signedOnNotOrdered != null ? [{ label: "Signed on, not ordered", value: c.signedOnNotOrdered, valueColor: AMBER }] : []),
        { label: "Ordering engineers", value: c.activeUsers, valueColor: PURPLE },
        { label: "Active engineers", value: c.activeEngineers ?? c.activeUsers, valueColor: CYAN, hint: "Ordering on either system. Target 812 on OSS by Friday." },
      ]} />
      <Card style={{ marginBottom: 8 }}>
        <SectionHeading title="Active-user journey" subtitle="Signed on → active on either system → ordering on OSS" />
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={journey} margin={{ top: 16, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="name" tick={AXIS} /><YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" name="Engineers" radius={[5, 5, 0, 0]}>
                {journey.map((r, i) => <Cell key={i} fill={r.fill} />)}
                <LabelList dataKey="value" position="top" fill="var(--color-text-2)" fontSize={13} fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <SectionDivider title="LPO transfer" />
      <KpiRow items={[
        { label: "Transfer — orders", value: `${tt.transferOrders ?? 0}%`, valueColor: pctColor(tt.transferOrders ?? 0), hint: "OSS orders ÷ (OSS + LPO) since the floor." },
        { label: "Transfer — users", value: `${tt.transferUsers ?? 0}%`, valueColor: pctColor(tt.transferUsers ?? 0) },
        { label: "OSS orders", value: tt.ssOrders?.toLocaleString(), valueColor: PURPLE },
        { label: "LPO orders", value: tt.lpoOrders?.toLocaleString(), valueColor: MUTE },
      ]} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 8 }}>
        <OssVsLpoCard />
        <EngineerUsageCard />
      </div>

      <SectionDivider title="Geography" subtitle="OSS vs LPO by engineer region — full map & drill-down on the Geography tab" />
      <KpiRow items={[
        { label: "Active regions", value: regsWithData.length, valueColor: "var(--color-text-1)", hint: "Regions with at least one OSS or LPO order." },
        { label: "Best adoption", value: topRegion ? `${topRegion.region} · ${topRegion.transferOrders}%` : "—", valueColor: GREEN, hint: "Region with the highest OSS share of orders." },
        { label: "Lowest adoption", value: bottomRegion ? `${bottomRegion.region} · ${bottomRegion.transferOrders}%` : "—", valueColor: AMBER, hint: "Region with the lowest OSS share — the one to target." },
        { label: "Reverters", value: totalReverted, valueColor: RED, hint: "Engineers who've placed an LPO since their last OSS order, across all regions." },
      ]} />
      <RegionLeagueTable />
    </>
  );
}

// Data export: download an Excel of every stored order field, filtered.
function DataDump() {
  const [meta, setMeta] = useState(null);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [supplier, setSupplier] = useState("");
  const [status, setStatus] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [granularity, setGranularity] = useState("line");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    apiGet("/supply-scanner/export/meta").then((d) => {
      setMeta(d);
      if (d?.minDate) setFrom(d.minDate);
      if (d?.maxDate) setTo(d.maxDate);
    }).catch(() => setMeta({ suppliers: [], statuses: [] }));
  }, []);

  const download = async () => {
    setBusy(true);
    try {
      const params = { include_deleted: includeDeleted, granularity };
      if (from) params.date_from = from;
      if (to) params.date_to = to;
      if (supplier) params.supplier = supplier;
      if (status) params.status = status;
      const res = await axios.get(`${API}/supply-scanner/export`, { params, responseType: "blob", timeout: 120000 });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `supply_scanner_orders_${from || "all"}_to_${to || "all"}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded.");
    } catch (e) {
      toast.error(e?.response?.status === 401 ? "Session expired, sign in again." : "Export failed.");
    } finally { setBusy(false); }
  };

  const inputStyle = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8, padding: "8px 10px", fontSize: 13 };
  const labelStyle = { fontSize: 12, color: MUTE, marginBottom: 6, display: "block" };

  return (
    <Card>
      <SectionHeading title="Data export"
        subtitle="Download an Excel of the orders held from the API. Line level gives one row per item (every line-item field); order level gives one row per order." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 16 }}>
        <div>
          <label style={labelStyle}>From</label>
          <input type="date" value={from} min={meta?.minDate || undefined} max={meta?.maxDate || undefined} onChange={(e) => setFrom(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
        </div>
        <div>
          <label style={labelStyle}>To</label>
          <input type="date" value={to} min={meta?.minDate || undefined} max={meta?.maxDate || undefined} onChange={(e) => setTo(e.target.value)} style={{ ...inputStyle, width: "100%" }} />
        </div>
        <div>
          <label style={labelStyle}>Supplier</label>
          <select value={supplier} onChange={(e) => setSupplier(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
            <option value="">All</option>
            {(meta?.suppliers || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label style={labelStyle}>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...inputStyle, width: "100%" }}>
            <option value="">All</option>
            {(meta?.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <span style={{ fontSize: 12, color: MUTE }}>Granularity</span>
        <div style={{ display: "inline-flex", gap: 2, background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)", borderRadius: 999, padding: 3 }}>
          {[["line", "Line level"], ["order", "Order level"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setGranularity(k)}
              style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "5px 14px", fontSize: 12.5, fontWeight: 600,
                background: granularity === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent",
                color: granularity === k ? "#fff" : "var(--color-text-3)" }}>{lbl}</button>
          ))}
        </div>
      </div>
      {granularity === "line" && meta && meta.hasLineItems === false && (
        <div style={{ fontSize: 12.5, color: AMBER, marginBottom: 12 }}>
          Line-item detail isn't stored yet. Run a <strong>Full refresh</strong> once to pull it from the API, then line-level export will populate.
        </div>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-2)", marginBottom: 16, cursor: "pointer" }}>
        <input type="checkbox" checked={includeDeleted} onChange={(e) => setIncludeDeleted(e.target.checked)} />
        Include deleted orders
      </label>
      <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
        <Button onClick={download} disabled={busy} className="btn btn-primary" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} {busy ? "Preparing…" : "Download Excel"}
        </Button>
        {meta && <span style={{ fontSize: 12.5, color: MUTE }}>{meta.count?.toLocaleString()} orders on record{meta.minDate ? `, ${meta.minDate} to ${meta.maxDate}` : ""}</span>}
      </div>
    </Card>
  );
}

// Per-supplier scorecard, dealt as a card: order metrics (volume, spend,
// completion) joined to Featurebase feedback (themes, causes, top issue,
// stopped-order rate). The accent + tier come from complaints-per-100 orders,
// so a quiet supplier literally looks calmer. Pick from the dropdown or click a
// row in the league table below.
const REDUCED = typeof window !== "undefined" && window.matchMedia
  && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Accents drawn from the app palette (green / cyan / purple / pink / amber) so
// the card sits in keeping with the rest of the dashboard.
const TIERS = {
  Excellent: { accent: "#34d399", accent2: "#67e0f0", glow: "rgba(52,211,153,0.5)", glowSoft: "rgba(103,224,240,0.18)", word: "Quiet supplier" },
  Good: { accent: "#67e0f0", accent2: "#a855f7", glow: "rgba(103,224,240,0.45)", glowSoft: "rgba(168,85,247,0.20)", word: "Steady supplier" },
  Watch: { accent: "#fbbf24", accent2: "#fb7185", glow: "rgba(251,191,36,0.5)", glowSoft: "rgba(251,113,133,0.22)", word: "Noisy supplier" },
  New: { accent: "#9d8fc0", accent2: "#6f6296", glow: "rgba(157,143,192,0.4)", glowSoft: "rgba(157,143,192,0.16)", word: "No feedback yet" },
};
const OWNED_CAUSES = new Set(["Supplier data", "Supplier ops"]);

function tierFor(cp) { return cp == null ? "New" : cp < 2.5 ? "Excellent" : cp <= 5 ? "Good" : "Watch"; }

const gbpCard = (v) => v == null ? "—" : v >= 1e6 ? "£" + (v / 1e6).toFixed(2) + "M" : v >= 1e3 ? "£" + Math.round(v / 1e3) + "k" : "£" + Math.round(v);

// Count-up number that animates once on mount.
function CountUp({ value, dp = 0, int = false, suffix = "", fallback = "—" }) {
  const [disp, setDisp] = useState(REDUCED ? value : 0);
  useEffect(() => {
    if (value == null || REDUCED) { setDisp(value); return; }
    let raf; const start = performance.now(), dur = 850;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setDisp(value * e);
      if (p < 1) raf = requestAnimationFrame(tick); else setDisp(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  if (value == null) return fallback;
  const out = int ? Math.round(disp).toLocaleString("en-GB") : disp.toFixed(dp);
  return out + suffix;
}

const SC_CSS = `
.sc-stage{ perspective:1400px; display:flex; justify-content:center; margin:2px 0 22px; }
.sc-card{ position:relative; width:min(460px,94vw); border-radius:22px; padding:22px 22px 20px;
  background:radial-gradient(135% 95% at 12% -8%, var(--sc-glow-soft) 0%, transparent 46%),
    linear-gradient(160deg,#271c46 0%,#1b1333 46%,#100a20 100%);
  border:1px solid #3a2966;
  box-shadow:0 20px 52px -20px var(--sc-glow), 0 28px 64px -34px rgba(0,0,0,.82), 0 0 0 1px rgba(255,255,255,.04) inset;
  --sc-ink:#faf8ff; --sc-ink-mid:#d3c7f2; --sc-ink-dim:#a99bd0;
  --sc-accent:#34d399; --sc-accent2:#67e0f0; --sc-glow:rgba(52,211,153,.55); --sc-glow-soft:rgba(103,224,240,.22);
  transform-style:preserve-3d; transition:transform .12s ease-out, box-shadow .3s ease; overflow:hidden;
  font-family:var(--font-family, system-ui, sans-serif); color:var(--sc-ink); }
.sc-card.sc-deal{ animation:sc-deal .6s cubic-bezier(.18,.9,.24,1) both; }
@keyframes sc-deal{ 0%{opacity:0;transform:translateY(24px) scale(.965);} 100%{opacity:1;transform:none;} }
.sc-card::before{ content:""; position:absolute; inset:6px; border-radius:16px; border:1px solid var(--sc-glow-soft); pointer-events:none; z-index:2; }
.sc-sheen{ position:absolute; inset:0; border-radius:22px; pointer-events:none; z-index:3; mix-blend-mode:soft-light; opacity:1;
  background:radial-gradient(480px 480px at var(--sc-mx,50%) var(--sc-my,0%), var(--sc-accent) 0%, transparent 44%),
    linear-gradient(115deg, transparent 26%, var(--sc-accent2) 48%, transparent 70%);
  background-size:100% 100%,240% 240%; background-position:0 0, var(--sc-mx,50%) 0; transition:opacity .3s ease; }
.sc-z{ position:relative; z-index:1; }
.sc-mono, .sc-tier-hp, .sc-big, .sc-sig-val, .sc-theme-count, .sc-lg-count, .sc-votes{ font-family:'IBM Plex Mono', ui-monospace, Menlo, monospace; font-variant-numeric:tabular-nums; }
.sc-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:14px; }
.sc-rank{ font-family:'IBM Plex Mono', monospace; font-size:11px; letter-spacing:.14em; color:var(--sc-ink-dim); text-transform:uppercase; margin:0 0 3px; }
.sc-name{ font-family:var(--font-display, 'Sora', system-ui, sans-serif); font-size:31px; font-weight:800; line-height:1.02; letter-spacing:-.015em; margin:0;
  background:linear-gradient(120deg,var(--sc-ink) 0%,var(--sc-ink) 55%,var(--sc-accent2) 130%); -webkit-background-clip:text; background-clip:text; color:transparent; }
.sc-status{ margin:7px 0 0; font-size:12px; color:var(--sc-ink-mid); line-height:1.4; }
.sc-status b{ color:var(--sc-accent2); font-weight:600; }
.sc-tier{ flex:0 0 auto; display:inline-flex; flex-direction:column; align-items:center; gap:3px; padding:9px 13px 8px; border-radius:13px; background:var(--sc-glow-soft); border:1px solid var(--sc-glow); box-shadow:0 0 26px -6px var(--sc-glow); }
.sc-tier-hp{ font-size:22px; font-weight:700; line-height:1; color:var(--sc-accent); text-shadow:0 0 16px var(--sc-glow); }
.sc-tier-label{ font-size:9px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:var(--sc-ink-dim); }
.sc-tier-name{ font-size:10px; font-weight:700; letter-spacing:.08em; text-transform:uppercase; color:var(--sc-accent2); }
.sc-hero{ margin-top:18px; display:grid; grid-template-columns:1fr 1fr 1fr; gap:10px; }
.sc-cell{ padding:12px 11px 11px; border-radius:13px; background:linear-gradient(160deg, rgba(255,255,255,.06), rgba(255,255,255,.02)); border:1px solid rgba(255,255,255,.1); }
.sc-big{ font-size:22px; font-weight:700; line-height:1; color:var(--sc-ink); text-shadow:0 0 20px var(--sc-glow-soft); }
.sc-unit{ font-size:13px; color:var(--sc-ink-mid); font-weight:500; }
.sc-cap{ margin-top:7px; font-size:9.5px; letter-spacing:.11em; text-transform:uppercase; color:var(--sc-ink-dim); font-weight:600; }
.sc-signals{ margin-top:12px; display:grid; grid-template-columns:1fr 1fr 1fr; border:1px solid rgba(255,255,255,.08); border-radius:13px; overflow:hidden; }
.sc-sig{ padding:11px 12px; background:rgba(255,255,255,.015); }
.sc-sig + .sc-sig{ border-left:1px solid rgba(255,255,255,.08); }
.sc-sig-val{ font-size:17px; font-weight:600; line-height:1; display:inline-flex; align-items:baseline; gap:5px; }
.sc-dot{ width:7px; height:7px; border-radius:50%; display:inline-block; align-self:center; background:currentColor; box-shadow:0 0 8px currentColor; }
.sc-sig-cap{ margin-top:6px; font-size:9px; letter-spacing:.08em; text-transform:uppercase; color:var(--sc-ink-dim); font-weight:600; }
.sc-sec-head{ display:flex; align-items:center; gap:9px; margin:18px 0 10px; }
.sc-sec-head h3{ margin:0; font-size:10px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; color:var(--sc-ink-mid); white-space:nowrap; }
.sc-rule{ flex:1; height:1px; background:rgba(255,255,255,.08); }
.sc-empty{ font-size:12.5px; color:var(--sc-ink-dim); }
.sc-themes{ display:flex; flex-direction:column; gap:9px; }
.sc-theme-top{ display:flex; justify-content:space-between; align-items:baseline; margin-bottom:5px; }
.sc-theme-label{ font-size:12px; font-weight:500; color:var(--sc-ink); }
.sc-theme-count{ font-size:11px; color:var(--sc-ink-dim); }
.sc-track{ height:6px; border-radius:4px; background:rgba(255,255,255,.06); overflow:hidden; }
.sc-track{ box-shadow:inset 0 0 0 1px rgba(255,255,255,.04); }
.sc-fill{ height:100%; width:0; border-radius:4px; background:linear-gradient(90deg,var(--sc-accent) 0%,var(--sc-accent2) 100%); box-shadow:0 0 12px -1px var(--sc-glow); transition:width .9s cubic-bezier(.2,.8,.2,1); }
.sc-cause-bar{ display:flex; height:12px; border-radius:6px; overflow:hidden; border:1px solid #2a1c47; background:#0f0a1e; }
.sc-cause-bar span{ width:0; transition:width .8s cubic-bezier(.2,.8,.2,1); }
.sc-cause-legend{ margin-top:11px; display:grid; grid-template-columns:1fr 1fr; gap:8px 14px; }
.sc-lg{ display:flex; align-items:center; gap:8px; font-size:11px; color:var(--sc-ink-mid); }
.sc-lg.owned{ color:var(--sc-ink); }
.sc-swatch{ width:10px; height:10px; border-radius:3px; flex:0 0 auto; }
.sc-lg-count{ margin-left:auto; font-size:11px; color:var(--sc-ink-dim); }
.sc-lg.owned .sc-lg-name::after{ content:"OWN"; margin-left:6px; font-family:'IBM Plex Mono',monospace; font-size:8px; letter-spacing:.1em; padding:1px 4px; border-radius:4px; color:var(--sc-accent2); border:1px solid var(--sc-glow-soft); vertical-align:middle; }
.sc-gripe{ margin-top:18px; padding:13px 15px; border-radius:13px; background:linear-gradient(180deg,rgba(255,255,255,.03),rgba(255,255,255,.008)); border:1px solid rgba(255,255,255,.08); border-left:2px solid var(--sc-accent); }
.sc-gripe-cap{ font-size:9px; letter-spacing:.14em; text-transform:uppercase; color:var(--sc-ink-dim); font-weight:700; display:flex; justify-content:space-between; align-items:center; }
.sc-votes{ color:var(--sc-accent2); letter-spacing:.04em; }
.sc-gripe-title{ margin:7px 0 0; font-size:13.5px; font-style:italic; color:var(--sc-ink); line-height:1.4; }
.sc-gripe-title a{ color:inherit; text-decoration:none; }
.sc-gripe-title a:hover{ text-decoration:underline; }
@media (prefers-reduced-motion: reduce){ .sc-card.sc-deal{ animation:none; } .sc-fill,.sc-cause-bar span,.sc-card{ transition:none; } .sc-sheen{ display:none; } }
`;

function Suppliers() {
  const [names, setNames] = useState(null);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState("");
  const [active, setActive] = useState("");   // the supplier currently revealed
  const [card, setCard] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadNames = useCallback(async () => {
    try {
      const d = await apiGet("/supply-scanner/supplier-names");
      const ns = d?.names || [];
      setNames(ns);
      setMeta(d?.feedback || null);
      setSelected((cur) => cur || ns[0] || "");
    } catch { setNames([]); }
  }, []);
  useEffect(() => { loadNames(); }, [loadNames]);

  const generate = useCallback((name) => {
    const who = name || selected;
    if (!who) return;
    setActive(who);
    setLoadingCard(true);
    apiGet(`/supply-scanner/suppliers/${encodeURIComponent(who)}`)
      .then(setCard).catch(() => setCard(null)).finally(() => setLoadingCard(false));
  }, [selected]);

  const sync = async () => {
    setSyncing(true);
    const before = meta?.lastAttempt || null;
    try {
      await apiPost("/supply-scanner/feedback/sync", {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start sync.");
      setSyncing(false);
      return;
    }
    // The pull runs in the background; poll meta until the attempt completes.
    let tries = 40; // ~2 min at 3s
    const poll = async () => {
      let m;
      try { m = await apiGet("/supply-scanner/feedback/meta"); } catch { m = null; }
      if (m) setMeta(m);
      if (m && !m.running && m.lastAttempt && m.lastAttempt !== before) {
        setSyncing(false);
        if (m.lastError) toast.error(`Sync failed: ${m.lastError}`);
        else {
          toast.success(`Feedback synced. ${m.count} posts.`);
          await loadNames();
          if (active) generate(active);
        }
        return;
      }
      if (tries-- > 0) setTimeout(poll, 3000);
      else { setSyncing(false); toast.message("Sync is taking a while — it'll finish in the background."); }
    };
    setTimeout(poll, 2500);
  };

  const syncedNote = meta?.lastSync
    ? `Feedback synced ${new Date(meta.lastSync).toLocaleString("en-GB")} · ${meta.count} posts`
    : (meta && !meta.configured ? "Featurebase not connected. Add the key in Settings." : "Feedback not synced yet");

  const selStyle = { appearance: "none", WebkitAppearance: "none", background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 12, padding: "12px 38px 12px 15px", fontSize: 14, fontWeight: 600, cursor: "pointer", minWidth: 220 };

  return (
    <div>
      <style>{SC_CSS}</style>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{ position: "relative", display: "inline-flex" }}>
            <select value={selected} onChange={(e) => setSelected(e.target.value)} style={selStyle} data-testid="supplier-select">
              {(names || []).length === 0 && <option value="">No suppliers</option>}
              {(names || []).map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
            <span style={{ position: "absolute", right: 15, top: "50%", width: 7, height: 7, borderRight: "2px solid " + MUTE, borderBottom: "2px solid " + MUTE, transform: "translateY(-70%) rotate(45deg)", pointerEvents: "none" }} />
          </div>
          <Button className="btn btn-primary" disabled={!selected} onClick={() => generate()} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="supplier-generate">
            Generate
          </Button>
        </div>
        <Button className="btn btn-secondary" disabled={syncing} onClick={sync} style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="feedback-sync-btn">
          {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {syncing ? "Syncing…" : "Sync feedback"}
        </Button>
      </div>
      <div style={{ fontSize: 12, color: MUTE, marginBottom: 16 }}>{syncedNote}</div>

      <div className="sc-stage">
        {loadingCard ? (
          <div className="sc-card" style={{ minHeight: 460, display: "flex", alignItems: "center", justifyContent: "center", color: MUTE }}>Dealing card…</div>
        ) : active && card ? (
          <SupplierCard key={active} raw={card} />
        ) : (
          <div className="sc-card" style={{ minHeight: 220, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", justifyContent: "center", textAlign: "center", color: MUTE, padding: "0 30px" }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700, color: "var(--color-text-2)" }}>Pick a supplier and hit Generate</div>
            <div style={{ fontSize: 12.5, maxWidth: 320, lineHeight: 1.5 }}>One card at a time, so no supplier ever sees how a competitor is doing.</div>
          </div>
        )}
      </div>
    </div>
  );
}

function SupplierCard({ raw }) {
  const cardRef = useRef(null);
  const sheenRef = useRef(null);
  const [reveal, setReveal] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReveal(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const onMove = (e) => {
    if (REDUCED || !cardRef.current) return;
    const r = cardRef.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
    cardRef.current.style.transform = `rotateX(${((0.5 - py) * 8).toFixed(2)}deg) rotateY(${((px - 0.5) * 10).toFixed(2)}deg)`;
    if (sheenRef.current) {
      sheenRef.current.style.setProperty("--sc-mx", (px * 100).toFixed(1) + "%");
      sheenRef.current.style.setProperty("--sc-my", (py * 100).toFixed(1) + "%");
      sheenRef.current.style.opacity = "1";
    }
  };
  const onLeave = () => {
    if (!cardRef.current) return;
    cardRef.current.style.transform = "";
    if (sheenRef.current) { sheenRef.current.style.opacity = "0.85"; sheenRef.current.style.setProperty("--sc-mx", "50%"); sheenRef.current.style.setProperty("--sc-my", "0%"); }
  };

  const o = raw.orders || {}, fb = raw.feedback || {};
  const cp = fb.complaintsPer100;
  const tierKey = tierFor(cp);
  const t = TIERS[tierKey];
  // cp is null when there are no OSS orders to divide by. Distinguish "has
  // feedback but not ordered via OSS" from genuinely no data.
  const tierName = cp == null ? (fb.count ? "Unrated" : "New") : tierKey;
  const tierWord = cp == null
    ? (fb.count ? `Not ordered via OSS · ${fb.count} feedback` : "No data yet")
    : t.word;
  const themes = (fb.themes || []).slice(0, 3);
  const themeMax = Math.max(1, ...themes.map((x) => x.count));
  const causes = fb.causes || [];
  const owned = causes.filter((c) => OWNED_CAUSES.has(c.label)).sort((a, b) => b.count - a.count)[0];
  const topIssue = (raw.topIssues || [])[0];
  const cpColour = cp == null ? MUTE : cp < 2.5 ? GREEN : cp <= 5 ? AMBER : RED;

  const cardVars = { "--sc-accent": t.accent, "--sc-accent2": t.accent2, "--sc-glow": t.glow, "--sc-glow-soft": t.glowSoft };

  return (
    <article ref={cardRef} className={`sc-card${reveal ? " sc-deal" : ""}`} style={cardVars} onPointerMove={onMove} onPointerLeave={onLeave} data-testid="supplier-card">
      <div ref={sheenRef} className="sc-sheen" aria-hidden="true" />
      <div className="sc-z">
        <div className="sc-head">
          <div style={{ minWidth: 0 }}>
            <div className="sc-rank">Merchant · OES</div>
            <h2 className="sc-name">{raw.supplier}</h2>
            <p className="sc-status"><b>{tierWord}.</b>{owned ? <> Weakest own fault: <b>{owned.label}</b>.</> : ""}</p>
          </div>
          <div className="sc-tier">
            <span className="sc-tier-hp"><CountUp value={o.completedRate} dp={0} /></span>
            <span className="sc-tier-label">Completed %</span>
            <span className="sc-tier-name">{tierName}</span>
          </div>
        </div>

        <div className="sc-hero">
          <div className="sc-cell"><div className="sc-big"><CountUp value={o.completedRate} dp={0} /><span className="sc-unit">%</span></div><div className="sc-cap">Completed rate</div></div>
          <div className="sc-cell"><div className="sc-big"><CountUp value={o.count} int /></div><div className="sc-cap">Orders</div></div>
          <div className="sc-cell"><div className="sc-big">{gbpCard(o.spend)}</div><div className="sc-cap">Spend</div></div>
        </div>

        <div className="sc-signals">
          <div className="sc-sig"><span className="sc-sig-val"><CountUp value={fb.count} int fallback="0" /></span><div className="sc-sig-cap">Feedback posts</div></div>
          <div className="sc-sig"><span className="sc-sig-val" style={{ color: cpColour }}><span className="sc-dot" style={{ color: cpColour }} /><CountUp value={cp} dp={1} /></span><div className="sc-sig-cap">Complaints / 100</div></div>
          <div className="sc-sig"><span className="sc-sig-val"><CountUp value={fb.stoppedRate} dp={0} /><span style={{ fontSize: 12, color: "var(--sc-ink-mid)" }}>%</span></span><div className="sc-sig-cap">Stopped an order</div></div>
        </div>

        <div className="sc-sec-head"><h3>What they're known for</h3><span className="sc-rule" /></div>
        {themes.length === 0 ? <div className="sc-empty">No tagged feedback yet.</div> : (
          <div className="sc-themes">
            {themes.map((th) => (
              <div className="sc-theme" key={th.label}>
                <div className="sc-theme-top"><span className="sc-theme-label">{th.label}</span><span className="sc-theme-count">{th.count}</span></div>
                <div className="sc-track"><div className="sc-fill" style={{ width: reveal ? `${Math.round(th.count / themeMax * 100)}%` : "0%" }} /></div>
              </div>
            ))}
          </div>
        )}

        {topIssue && (
          <div className="sc-gripe">
            <div className="sc-gripe-cap"><span>Biggest gripe</span><span className="sc-votes">▲ {topIssue.upvotes}</span></div>
            <p className="sc-gripe-title">{topIssue.url ? <a href={topIssue.url} target="_blank" rel="noreferrer">“{topIssue.title}”</a> : `“${topIssue.title}”`}</p>
          </div>
        )}
      </div>
    </article>
  );
}

// Section header for the single scrolling dashboard.
function SectionDivider({ title, subtitle }) {
  return (
    <div style={{ margin: "30px 0 14px", borderTop: "1px solid var(--hairline)", paddingTop: 18 }}>
      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--color-text-1)" }}>{title}</div>
      {subtitle && <div style={{ fontSize: 12.5, color: MUTE, marginTop: 3 }}>{subtitle}</div>}
    </div>
  );
}

// ── main page ───────────────────────────────────────────────────────────────
export default function SupplyScanner() {
  usePageTitle("Supply Scanner");
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);           // all-time base (has availableMonths)
  const [month, setMonth] = useState("");             // "" = All time (default view)
  const [scopedStats, setScopedStats] = useState(null); // stats scoped to `month`
  const [scoping, setScoping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState(null); // { percent, message } during Full refresh
  const pollRef = useRef(null);

  const loadStats = useCallback(async () => {
    const d = await apiGet("/supply-scanner/stats");
    setStats(d || null);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await loadStats(); } finally { setLoading(false); }
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [loadStats]);

  // Rescope the WHOLE dashboard when a month is picked; empty reuses all-time.
  useEffect(() => {
    let cancelled = false;
    if (!month) { setScopedStats(stats); return undefined; }
    (async () => {
      setScoping(true);
      const d = await apiGet("/supply-scanner/stats", { params: { month } });
      if (!cancelled) { if (d) setScopedStats(d); setScoping(false); }
    })();
    return () => { cancelled = true; };
  }, [month, stats]);

  const quickRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await apiPost("/supply-scanner/refresh");
      if (res?.stats) setStats(res.stats);
      toast.success("Orders refreshed from Supply Scanner.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Refresh failed — check server credentials / connectivity.");
    } finally { setRefreshing(false); }
  };

  const fullRefresh = async () => {
    setRefreshing(true);
    setProgress({ percent: 0, message: "Starting…" });
    try {
      await apiPost("/supply-scanner/refresh?full=true");
      pollRef.current = setInterval(async () => {
        const s = await apiGet("/supply-scanner/refresh/status");
        if (!s) return;
        setProgress({ percent: s.percent ?? 0, message: s.message || "Resolving order locations…" });
        if (s.state && s.state !== "running") {
          clearInterval(pollRef.current); pollRef.current = null;
          setRefreshing(false);
          setProgress(null);
          if (s.state === "done") {
            await loadStats();
            toast.success("Full refresh complete — real order locations resolved.");
          } else {
            toast.error(s.message || "Full refresh failed.");
          }
        }
      }, 2000);
    } catch (e) {
      setRefreshing(false);
      setProgress(null);
      toast.error(e?.response?.data?.detail || "Could not start full refresh.");
    }
  };

  if (loading) {
    return (
      <div data-testid="supply-scanner-page">
        <PageHeader title="Supply Scanner" subtitle="Order & engineer-adoption analytics" />
        <div style={{ color: MUTE, padding: 40 }}>Loading…</div>
      </div>
    );
  }

  const c = stats?.cards || {};
  const noData = !stats || (c.totalOrders || 0) === 0;
  const months = stats?.availableMonths || [];
  const activeStats = scopedStats || stats;

  const refreshBtns = (
    <div style={{ display: "flex", gap: 8 }}>
      <Button onClick={() => window.open("/supply-scanner/tv", "_blank")} className="btn btn-secondary"
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="ss-tv-view" title="Full-screen dashboard for a wall TV">
        <Tv size={15} /> TV view
      </Button>
      <Button onClick={quickRefresh} disabled={refreshing} className="btn btn-secondary"
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="ss-refresh">
        {refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh
      </Button>
      <Button onClick={fullRefresh} disabled={refreshing} className="btn btn-primary"
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="ss-full-refresh">
        {progress
          ? <><Loader2 size={15} className="animate-spin" /> Refreshing… {progress.percent}%</>
          : <><MapPin size={15} /> Full refresh</>}
      </Button>
    </div>
  );

  return (
    <div data-testid="supply-scanner-page">
      <PageHeader
        title="Supply Scanner"
        subtitle={stats?.lastUpdated ? `Last updated ${new Date(stats.lastUpdated).toLocaleString("en-GB")}${stats?.source === "api" ? " · via API" : ""}` : "Order & engineer-adoption analytics"}
        right={refreshBtns}
      />

      <SubTabs
        style={{ marginBottom: 12 }}
        active={tab}
        onChange={setTab}
        tabs={[
          { key: "overview", label: "Overview" },
          { key: "spend", label: "Spend" },
          { key: "success", label: "Order success" },
          { key: "suppliers", label: "Suppliers" },
          { key: "adoption", label: "Adoption" },
          { key: "transfer", label: "LPO transfer" },
          { key: "heatmap", label: "Geography" },
          { key: "export", label: "Data export" },
        ]}
      />

      {!noData && (
        <PeriodPicker
          month={month}
          onChange={setMonth}
          months={months}
          scoping={scoping}
          disabled={tab === "heatmap" || tab === "export" || tab === "suppliers"}
          disabledNote={tab === "heatmap" ? "Geography is always all-time" : (tab === "export" ? "Data export has its own date range" : (tab === "suppliers" ? "Supplier scorecard is all-time" : null))}
        />
      )}

      {noData && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ color: MUTE, fontSize: 14 }}>
            No order data cached yet. Hit <strong style={{ color: "var(--color-text-1)" }}>Refresh</strong> to pull live
            orders (the server needs Supply Scanner credentials configured).
          </div>
        </Card>
      )}

      {tab === "overview" && activeStats && <Overview stats={activeStats} month={month} />}
      {tab === "spend" && <Spend month={month} />}
      {tab === "success" && activeStats && <OrderSuccess stats={activeStats} />}
      {tab === "suppliers" && <Suppliers />}
      {tab === "adoption" && activeStats && <Adoption stats={activeStats} />}
      {tab === "transfer" && <Transfer month={month} />}
      {tab === "heatmap" && <MapPanel progress={progress} />}
      {tab === "export" && <DataDump />}
    </div>
  );
}

// Shared period picker — sits under the sub-tabs and drives the whole dashboard.
function PeriodPicker({ month, onChange, months, scoping, disabled, disabledNote }) {
  const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(Number(y), Number(mo) - 1, 1).toLocaleString("en-GB", { month: "short", year: "2-digit" }); };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
      <span style={{ fontSize: 13, color: MUTE }}>Period</span>
      <select value={month} onChange={(e) => onChange(e.target.value)} data-testid="ss-period-picker" disabled={disabled}
        style={{ background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8,
          padding: "6px 10px", fontSize: 13, opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}>
        <option value="">All time</option>
        {[...months].reverse().map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
      </select>
      {scoping && <Loader2 size={14} className="animate-spin" style={{ color: MUTE }} />}
      {!month && !scoping && !disabled && <span style={{ fontSize: 12, color: MUTE }}>since first OSS order</span>}
      {disabledNote && <span style={{ fontSize: 12, color: MUTE }}>{disabledNote}</span>}
    </div>
  );
}

// ── Spend section ────────────────────────────────────────────────────────────
const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const gbpTick = (v) => (v >= 1000 ? `£${(v / 1000).toLocaleString("en-GB", { maximumFractionDigits: v >= 10000 ? 0 : 1 })}k` : `£${v}`);

function Spend({ month }) {
  const [basis, setBasis] = useState("successful");
  const [d, setD] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setD(null);
    (async () => {
      const r = await apiGet("/supply-scanner/spend", { params: { ...(month ? { month } : {}), basis } }).catch(() => null);
      if (!cancelled) setD(r || { cards: {}, trend: [] });
    })();
    return () => { cancelled = true; };
  }, [month, basis]);

  const basisPill = (
    <div style={{ display: "inline-flex", gap: 2, background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)", borderRadius: 999, padding: 3 }}>
      {[["successful", "Successful only"], ["all", "All orders"]].map(([k, lbl]) => (
        <button key={k} onClick={() => setBasis(k)} data-testid={`spend-basis-${k}`}
          style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "5px 14px", fontSize: 12.5, fontWeight: 600,
            background: basis === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent",
            color: basis === k ? "#fff" : "var(--color-text-3)" }}>{lbl}</button>
      ))}
    </div>
  );

  if (!d) return <Card><div style={{ color: MUTE, padding: 24 }}>Loading spend…</div></Card>;
  const c = d.cards || {};
  const trend = d.trend || [];
  const daily = d.daily || [];
  const sparse = trend.length > 0 && trend.length <= 6;
  const fulfil = [{ name: "Collection", value: d.fulfillment?.collection || 0 }, { name: "Delivery", value: d.fulfillment?.delivery || 0 }];
  const statusRows = (d.byStatus || []).map((r) => ({ ...r, label: statusLabel(r.status) }));

  const kpis = [
    { label: "Total spend", value: gbp0(c.totalSpend), valueColor: GREEN, hint: `Sum of order value (${d.basis === "all" ? "all orders" : "successful orders"}), since the Aug-31 floor or the selected month.` },
    { label: "Total orders", value: c.totalOrders?.toLocaleString() ?? c.orders?.toLocaleString(), valueColor: "var(--color-text-1)", hint: `Orders counted for spend (${d.basis === "all" ? "all statuses" : "successful only"}).` },
    { label: "Avg order value", value: gbp2(c.avgOrderValue), valueColor: CYAN, hint: "Total spend ÷ orders counted." },
    { label: "Median order", value: gbp2(c.medianOrderValue), valueColor: CYAN, hint: "The typical order — robust to a few very large orders that drag the mean." },
    { label: "Avg orders / engineer", value: c.avgOrdersPerEngineer, valueColor: PURPLE, hint: "Orders ÷ distinct ordering engineers." },
    { label: "Avg items / order", value: c.avgItemsPerOrder, valueColor: PURPLE, hint: "Mean line items per order (basket size)." },
    { label: "Spend this week", value: gbp0(c.spendThisWeek), valueColor: CYAN, hint: "Order value since Monday of the current UK week — live, regardless of the selected month." },
    { label: "Orders this week", value: c.ordersThisWeek?.toLocaleString(), valueColor: CYAN, hint: "Orders since Monday of the current UK week." },
    { label: "Spend today", value: gbp0(c.spendToday), valueColor: GREEN, hint: "Order value placed today (UK time)." },
    { label: "Orders today", value: c.ordersToday?.toLocaleString(), valueColor: GREEN, hint: "Orders placed today (UK time)." },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <span style={{ fontSize: 12.5, color: MUTE }}>OSS spend only — LPO carries no order value.</span>
        {basisPill}
      </div>
      <KpiRow items={kpis} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {chartCard("Spend over time",
          <LineChart data={trend} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} /><YAxis tick={AXIS} tickFormatter={gbpTick} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => gbp2(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="spend" name="Monthly" stroke={GREEN} strokeWidth={2} dot={sparse ? { r: 4, fill: GREEN, strokeWidth: 0 } : false} />
            <Line type="monotone" dataKey="cumulativeSpend" name="Cumulative" stroke={PINK} strokeWidth={2} dot={sparse ? { r: 4, fill: PINK, strokeWidth: 0 } : false} />
          </LineChart>, `Monthly and running total (${d.basis === "all" ? "all orders" : "successful only"})`)}

        {chartCard("Daily spend (last 3 months)",
          <LineChart data={daily} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} minTickGap={24} /><YAxis tick={AXIS} tickFormatter={gbpTick} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => gbp2(v)} />
            <Line type="monotone" dataKey="spend" name="Daily spend" stroke={CYAN} strokeWidth={2} dot={daily.length <= 6 ? { r: 3, fill: CYAN, strokeWidth: 0 } : false} />
          </LineChart>, "Day-on-day OSS spend")}
      </div>

      <div style={{ marginBottom: 16 }}>
        {chartCard("Avg order value over time",
          <LineChart data={trend} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} /><YAxis tick={AXIS} tickFormatter={gbpTick} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => gbp2(v)} />
            <Line type="monotone" dataKey="avgOrderValue" name="Avg order value" stroke={CYAN} strokeWidth={2} dot={sparse ? { r: 4, fill: CYAN, strokeWidth: 0 } : false} />
          </LineChart>, "Mean order total per month")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {chartCard("Spend by supplier",
          <BarChart data={d.bySupplier || []} margin={{ top: 6, right: 12, left: 4, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="supplier" tick={AXIS} interval={0} angle={-18} textAnchor="end" height={54} />
            <YAxis tick={AXIS} tickFormatter={gbpTick} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} formatter={(v) => gbp2(v)} />
            <Bar dataKey="spend" name="Spend" radius={[4, 4, 0, 0]}>
              {(d.bySupplier || []).map((_, i) => <Cell key={i} fill={SUPPLIER_COLORS[i % SUPPLIER_COLORS.length]} />)}
            </Bar>
          </BarChart>, "Share of spend by supplier")}

        {chartCard("Order value distribution",
          <BarChart data={d.distribution || []} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="bucket" tick={AXIS} /><YAxis tick={AXIS} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }}
              formatter={(v, n, p) => [`${v} orders · ${gbp0(p.payload.spend)}`, "Orders"]} />
            <Bar dataKey="count" name="Orders" fill={PURPLE} radius={[4, 4, 0, 0]} />
          </BarChart>, "How many orders fall in each value band")}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {chartCard("Spend by order status",
          <BarChart data={statusRows} layout="vertical" margin={{ top: 6, right: 20, left: 40, bottom: 0 }}>
            <XAxis type="number" tick={AXIS} tickFormatter={gbpTick} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={120} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} formatter={(v) => gbp2(v)} />
            <Bar dataKey="spend" name="Spend" radius={[0, 5, 5, 0]}>
              {statusRows.map((r, i) => <Cell key={i} fill={STATUS_COLORS[r.status] || MUTE} />)}
            </Bar>
          </BarChart>, "Where the money sits — completed, in-flight, cancelled")}

        {chartCard("Collection vs delivery",
          <PieChart>
            <Pie data={fulfil} dataKey="value" nameKey="name" innerRadius={52} outerRadius={88} paddingAngle={2}>
              <Cell fill={CYAN} /><Cell fill={PINK} />
            </Pie>
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => gbp2(v)} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>, "Spend split by fulfilment method")}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeading title="Spend by region" subtitle="Engineer region (from the region map) — since the Aug-31 floor" />
        <DataTable perPage={15} searchKeys={["region"]} searchPlaceholder="Search region…" defaultSortKey="spend" defaultSortDir="desc"
          columns={[
            { key: "region", label: "Region", width: "1.4fr" },
            { key: "spend", label: "Spend", align: "right", width: "1fr", render: (r) => <span style={{ color: GREEN, fontWeight: 600 }}>{gbp0(r.spend)}</span> },
            { key: "orders", label: "Orders", align: "right", width: "0.8fr" },
            { key: "avg", label: "Avg order", align: "right", width: "0.9fr", sortValue: (r) => (r.orders ? r.spend / r.orders : 0), render: (r) => gbp2(r.orders ? r.spend / r.orders : 0) },
          ]}
          rows={d.byRegion || []} />
      </Card>

      <Card>
        <SectionHeading title="Top spending engineers" subtitle="By total OSS spend" />
        <DataTable perPage={10} searchKeys={["name"]} searchPlaceholder="Search engineer…" defaultSortKey="spend" defaultSortDir="desc"
          columns={[
            { key: "name", label: "Engineer", width: "1.6fr" },
            { key: "spend", label: "Spend", align: "right", width: "1fr", render: (r) => <span style={{ color: GREEN, fontWeight: 600 }}>{gbp0(r.spend)}</span> },
            { key: "orders", label: "Orders", align: "right", width: "0.8fr" },
            { key: "avg", label: "Avg order", align: "right", width: "0.9fr", sortValue: (r) => (r.orders ? r.spend / r.orders : 0), render: (r) => gbp2(r.orders ? r.spend / r.orders : 0) },
          ]}
          rows={d.topEngineers || []} />
      </Card>
    </>
  );
}


// ── Order-success deep-dive tab ──────────────────────────────────────────────
function OrderSuccess({ stats }) {
  const total = stats.cards.totalOrders || 1;
  const statusRows = Object.entries(stats.statusCounts)
    .filter(([, v]) => v > 0)
    .map(([status, count]) => ({ status, label: statusLabel(status), count, pct: Math.round((count / total) * 100) }));

  return (
    <>
      <KpiRow
        items={[
          { label: "Order completed", value: `${stats.cards.successRate}%`, valueColor: stats.cards.successRate >= 75 ? GREEN : AMBER,
            hint: "Completed orders as a share of completed + cancelled (successful vs cancelled). In-process and submission failures are excluded, so it isn't dragged down by orders sitting in Processing." },
          { label: "Completed", value: stats.statusCounts.Completed, valueColor: GREEN,
            hint: "OSS orders whose current status is 'Completed' — supplier accepted and fulfilled." },
          { label: "Submission failed", value: stats.statusCounts["Submission Failed"], valueColor: RED,
            hint: "OSS orders that never reached the supplier — typically a validation or connectivity failure at submission." },
          { label: "Order cancelled", value: stats.statusCounts["Fulfillment Declined"], valueColor: AMBER,
            hint: "Orders cancelled after submission — by the engineer (rang the branch) or the supplier. Not a system failure; only 'Submission failed' is a true failure." },
        ]}
      />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {chartCard("Status breakdown",
          <BarChart data={statusRows} layout="vertical" margin={{ top: 6, right: 20, left: 40, bottom: 0 }}>
            <XAxis type="number" tick={AXIS} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={120} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
            <Bar dataKey="count" radius={[0, 5, 5, 0]}>
              {statusRows.map((r, i) => <Cell key={i} fill={STATUS_COLORS[r.status] || MUTE} />)}
            </Bar>
          </BarChart>)}
        {chartCard("Collection vs delivery",
          <PieChart>
            <Pie data={[{ name: "Collection", value: stats.fulfillment.collection }, { name: "Delivery", value: stats.fulfillment.delivery }]}
              dataKey="value" nameKey="name" innerRadius={52} outerRadius={88} paddingAngle={2}>
              <Cell fill={CYAN} /><Cell fill={PINK} />
            </Pie>
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} /><Legend wrapperStyle={{ fontSize: 11 }} />
          </PieChart>)}
      </div>
      <Card>
        <SectionHeading title="Success by supplier" subtitle="Success = completed ÷ (completed + true failures). 'In process' and cancellations are excluded, so suppliers who just don't close statuses aren't punished." />
        <DataTable
          perPage={10}
          columns={[
            { key: "supplier", label: "Supplier", width: "1.5fr" },
            { key: "total", label: "Orders", align: "right", width: "0.6fr" },
            { key: "completed", label: "Completed", align: "right", width: "0.8fr" },
            { key: "inProcess", label: "In process", align: "right", width: "0.8fr",
              render: (r) => <span style={{ color: CYAN }}>{r.inProcess ?? 0}</span> },
            { key: "cancelled", label: "Cancelled", align: "right", width: "0.8fr",
              render: (r) => <span style={{ color: AMBER }}>{r.cancelled ?? 0}</span> },
            { key: "failed", label: "Failed", align: "right", width: "0.6fr",
              render: (r) => <span style={{ color: (r.failed || 0) > 0 ? RED : MUTE }}>{r.failed ?? 0}</span> },
            { key: "successPct", label: "Success %", align: "right", width: "0.9fr",
              render: (r) => <StatusBadge value={r.successPct ?? r.completedPct} good={90} ok={75} /> },
          ]}
          rows={stats.coverage}
        />
      </Card>
    </>
  );
}

// ── Adoption tab ─────────────────────────────────────────────────────────────
function Adoption({ stats }) {
  const c = stats.cards;
  const onboarded = c.onboardedEngineers ?? c.knownLoggedInUsers;
  const signedOn = c.signedOnEngineers; // present only when WorkOS supplied it
  const gap = stats.adoptionGap || [];
  // Onboarded engineers who've touched neither OSS nor LPO — replaces the old
  // OSS-only "not yet ordering" gap (which flagged heavy LPO users as idle).
  // Falls back to the OSS-only gap if the backend hasn't sent the field yet.
  const never = stats.neverInteracted || gap;
  // Active-user journey (Gareth's redesign): signed on → active on either system
  // → ordering on OSS. Replaces the onboarded-led funnel that was dominated by
  // the inactive 2,229.
  const journey = [
    { name: "Signed on", value: signedOn ?? c.activeUsers },
    { name: "Active (OSS+LPO)", value: c.activeEngineers ?? c.activeUsers },
    { name: "Ordering on OSS", value: c.activeUsers },
  ].map((f, i) => ({ ...f, fill: [CYAN, PURPLE, GREEN][i] }));
  const kpisTop = [
    { label: "Adoption", value: `${c.adoptionRateActive ?? 0}%`, valueColor: (c.adoptionRateActive ?? 0) >= 50 ? GREEN : AMBER,
      hint: "Of everyone ordering on either system, the share on OSS (ordering engineers ÷ active engineers OSS+LPO). The headline migration measure." },
    ...(c.signedOnNotOrdered != null ? [{ label: "Signed on, not ordered", value: c.signedOnNotOrdered, valueColor: AMBER,
      hint: "Signed in to OSS but never placed an order — the addressable chase list. Signed on minus ordering engineers." }] : []),
    { label: "Ordering engineers", value: c.activeUsers, valueColor: PURPLE,
      hint: "Distinct engineers who have placed at least one OSS order in the selected period." },
    { label: "Active engineers", value: c.activeEngineers ?? c.activeUsers, valueColor: CYAN,
      hint: "Distinct engineers ordering on EITHER OSS or LPO. Target: 812 on OSS by Friday." },
  ];
  const kpisMore = [
    ...(signedOn != null ? [{ label: "Signed on", value: signedOn, valueColor: CYAN,
      hint: "Onboarded engineers who have signed in to OSS at least once. From WorkOS." }] : []),
    { label: `Onboarded${stats.onboardedSource === "workos" ? " (WorkOS)" : ""}`, value: onboarded, valueColor: PINK,
      hint: stats.onboardedSource === "workos"
        ? "Engineers currently in the WorkOS roster who should be ordering via OSS."
        : "Engineers on the fallback static roster. Set WORKOS_API_KEY to use the live onboarded list." },
    { label: "Never interacted", value: never.length, valueColor: RED,
      hint: "Onboarded engineers with zero OSS AND zero LPO orders — genuinely not using either system." },
  ];
  return (
    <>
      <KpiRow items={kpisTop} />
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--color-text-4)", margin: "4px 2px 8px" }}>More detail</div>
      <KpiRow items={kpisMore} />
      {stats.onboardedSource !== "workos" && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: MUTE }}>
            Using the fallback engineer list — set <code>WORKOS_API_KEY</code> on the server to measure adoption against the
            live onboarded roster.
          </div>
        </Card>
      )}
      <Card>
        <SectionHeading title="Active-user journey" subtitle="Signed on → active on either system → ordering on OSS" />
        <div style={{ width: "100%", height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={journey} margin={{ top: 16, right: 12, left: -8, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="name" tick={AXIS} /><YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" name="Engineers" radius={[5, 5, 0, 0]}>
                {journey.map((r, i) => <Cell key={i} fill={r.fill} />)}
                <LabelList dataKey="value" position="top" fill="var(--color-text-2)" fontSize={13} fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </>
  );
}

// ── LPO transfer tab ─────────────────────────────────────────────────────────
// Tracks migration off the legacy LPO process onto OSS/Supply Scanner.
//   transfer % = SS / (SS + LPO). LPO data is uploaded from the PO Requests sheet.
const pctColor = (v) => (v >= 50 ? GREEN : v >= 20 ? AMBER : v >= 5 ? CYAN : RED);

// Engineers who've reverted to LPO (an LPO order since their last OSS), by region
// — the engagement targeting report Gareth asked for.
function RegionReverterTable() {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await apiGet("/supply-scanner/regions").catch(() => null);
      if (!cancelled) setD(r || { regions: [] });
    })();
    return () => { cancelled = true; };
  }, []);
  const rows = (d?.regions || []).filter((r) => r.region !== "Unknown");
  const totalRev = rows.reduce((a, r) => a + (r.reverted || 0), 0);
  return (
    <Card style={{ marginBottom: 16 }}>
      <SectionHeading title="Reverters by region"
        subtitle={`${totalRev} engineers have placed an LPO since their last OSS order — where they are, to target engagement`} />
      <DataTable perPage={15} searchKeys={["region"]} searchPlaceholder="Search region…" defaultSortKey="reverted" defaultSortDir="desc"
        columns={[
          { key: "region", label: "Region", width: "1.4fr" },
          { key: "reverted", label: "Reverted", align: "right", width: "0.8fr",
            render: (r) => <span style={{ color: r.reverted > 0 ? RED : MUTE, fontWeight: 700 }}>{r.reverted}</span> },
          { key: "lpoOrders", label: "LPO orders", align: "right", width: "0.9fr",
            render: (r) => <span style={{ color: AMBER }}>{r.lpoOrders?.toLocaleString()}</span> },
          { key: "transferOrders", label: "% on OSS", align: "right", width: "0.8fr",
            render: (r) => <span style={{ color: pctColor(r.transferOrders), fontWeight: 600 }}>{r.transferOrders}%</span> },
        ]}
        rows={rows} />
      <div style={{ fontSize: 12, color: MUTE, marginTop: 10 }}>Use the Geography section's “Who?” button to see exactly which engineers.</div>
    </Card>
  );
}

// Pill selector shared by the OSS-vs-LPO and engineer-usage cards.
function PillSelect({ value, onChange, options, testidPrefix }) {
  return (
    <div style={{ display: "inline-flex", gap: 2, background: "var(--color-bg-panel, #0f0a1e)", border: "1px solid var(--hairline)", borderRadius: 999, padding: 3 }}>
      {options.map(([k, lbl]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testidPrefix}-${k}`}
          style={{ border: "none", cursor: "pointer", borderRadius: 999, padding: "5px 12px", fontSize: 12, fontWeight: 600,
            background: value === k ? "linear-gradient(90deg,#a855f7,#ec4899)" : "transparent",
            color: value === k ? "#fff" : "var(--color-text-3)" }}>{lbl}</button>
      ))}
    </div>
  );
}

// First week of the CURRENT sustained run where OSS out-orders LPO — the
// milestone week OSS overtook LPO. Null when LPO still leads, or when OSS led
// from the very start (nothing was overtaken).
export function crossoverLabel(series) {
  if (!series || !series.length) return null;
  const last = series[series.length - 1];
  if ((last.oss || 0) <= (last.lpo || 0)) return null;
  let i = series.length - 1;
  while (i > 0 && (series[i - 1].oss || 0) > (series[i - 1].lpo || 0)) i--;
  return i > 0 ? series[i].label : null;
}

// Orders placed on each system, bucketed weekly / monthly / yearly / all-time.
function OssVsLpoCard() {
  const [gran, setGran] = useState("weekly");
  const [d, setD] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await apiGet("/supply-scanner/oss-vs-lpo", { params: { granularity: gran } });
      if (!cancelled) setD(res || { series: [] });
    })();
    return () => { cancelled = true; };
  }, [gran]);

  const series = (d?.series || []).map((s) => ({ ...s, total: (s.oss || 0) + (s.lpo || 0) }));
  // With only a few periods (e.g. just after the Aug-31 data floor) a line has
  // nothing to connect, so show value-labelled dots instead of an empty chart.
  const sparse = series.length > 0 && series.length <= 6;
  const needsReupload = d && gran === "weekly" && d.hasLpo && !d.weeklyLpoAvailable;
  const isWeekly = gran === "weekly";
  const crossover = isWeekly ? crossoverLabel(series) : null;

  return (
    <Card data-testid="oss-vs-lpo-card">
      <SectionHeading title="Orders — OSS vs LPO" subtitle="Order volume placed on each system"
        right={<PillSelect value={gran} onChange={setGran} testidPrefix="oss-vs-lpo"
          options={[["weekly", "Weekly"], ["monthly", "Monthly"], ["yearly", "Yearly"], ["all", "All time"]]} />} />
      <div style={{ width: "100%", height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          {isWeekly ? (
            // Weekly can span 70+ buckets — smooth areas read far better than
            // a forest of grouped bars, and ticks stay sparse and clean.
            <ComposedChart data={series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
              <defs>
                <linearGradient id="ossVsLpoOssFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={PURPLE} stopOpacity={0.4} />
                  <stop offset="100%" stopColor={PURPLE} stopOpacity={0.04} />
                </linearGradient>
                <linearGradient id="ossVsLpoLpoFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={AMBER} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={AMBER} stopOpacity={0.04} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} minTickGap={40} />
              <YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL}
                labelFormatter={(l) => `Week commencing ${l}`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {crossover && (
                <ReferenceLine x={crossover} stroke={GREEN} strokeDasharray="5 4" strokeWidth={1.5}
                  label={{ value: "OSS overtakes LPO", position: "left", angle: -90, fill: GREEN, fontSize: 10.5, fontWeight: 700, offset: 10 }} />
              )}
              <Area type="monotone" dataKey="oss" name="OSS" stroke={PURPLE} strokeWidth={2} fill="url(#ossVsLpoOssFill)" dot={sparse ? { r: 4, fill: PURPLE, strokeWidth: 0 } : false}>
                {sparse && <LabelList dataKey="oss" position="top" fill={PURPLE} fontSize={11} fontWeight={700} />}
              </Area>
              <Area type="monotone" dataKey="lpo" name="LPO" stroke={AMBER} strokeWidth={2} fill="url(#ossVsLpoLpoFill)" dot={sparse ? { r: 4, fill: AMBER, strokeWidth: 0 } : false}>
                {sparse && <LabelList dataKey="lpo" position="bottom" fill={AMBER} fontSize={11} fontWeight={700} />}
              </Area>
              <Line type="monotone" dataKey="total" name="Total" stroke="var(--color-text-1)" strokeWidth={2} strokeDasharray="5 3" dot={false} />
            </ComposedChart>
          ) : (
            <ComposedChart data={series} margin={{ top: 12, right: 12, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#241a3d" vertical={false} />
              <XAxis dataKey="label" tick={AXIS} interval="preserveStartEnd" />
              <YAxis tick={AXIS} />
              <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="oss" name="OSS" fill={PURPLE} radius={[4, 4, 0, 0]}>
                {sparse && <LabelList dataKey="oss" position="top" fill={PURPLE} fontSize={11} fontWeight={700} />}
              </Bar>
              <Bar dataKey="lpo" name="LPO" fill={AMBER} radius={[4, 4, 0, 0]}>
                {sparse && <LabelList dataKey="lpo" position="top" fill={AMBER} fontSize={11} fontWeight={700} />}
              </Bar>
              <Line type="monotone" dataKey="total" name="Total" stroke="var(--color-text-1)" strokeWidth={2} strokeDasharray="5 3" dot={false} />
            </ComposedChart>
          )}
        </ResponsiveContainer>
      </div>
      {isWeekly && crossover && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 12.5, fontWeight: 600,
          color: GREEN, background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.3)",
          borderRadius: 999, padding: "5px 12px" }} data-testid="oss-crossover-note">
          <span style={{ width: 8, height: 8, borderRadius: 999, background: GREEN }} />
          Milestone — OSS overtook LPO in the week commencing {crossover} and has led since
        </div>
      )}
      {needsReupload && (
        <div style={{ fontSize: 12, color: AMBER, marginTop: 10 }} data-testid="oss-vs-lpo-reupload-note">
          Weekly LPO counts need a fresh PO Requests upload — the stored LPO data predates weekly tracking, so only OSS bars show per week.
        </div>
      )}
    </Card>
  );
}

// Engineer segmentation: LPO only / OSS only / Both / Neither for a period.
const USAGE_SEGS = [
  { key: "both", label: "Both", color: GREEN },
  { key: "ossOnly", label: "OSS only", color: PURPLE },
  { key: "lpoOnly", label: "LPO only", color: AMBER },
  { key: "neither", label: "Neither", color: "#5b4b7a" },
];

function EngineerUsageCard() {
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  const [openSeg, setOpenSeg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await apiGet("/supply-scanner/engineer-usage", { params: { period } });
      if (!cancelled) setD(res || null);
    })();
    return () => { cancelled = true; };
  }, [period]);

  const counts = d?.counts || {};
  const pieData = USAGE_SEGS.map((s) => ({ name: s.label, value: counts[s.key] || 0, color: s.color })).filter((s) => s.value > 0);
  const needsReupload = d && period === "week" && d.hasLpo && !d.weeklyLpoAvailable;

  return (
    <Card data-testid="engineer-usage-card">
      <SectionHeading title="Engineer usage" subtitle="Who orders via LPO, OSS, both — or neither"
        right={<PillSelect value={period} onChange={setPeriod} testidPrefix="engineer-usage"
          options={[["week", "This week"], ["month", "This month"], ["year", "This year"], ["all", "All time"]]} />} />
      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
        <div style={{ width: 180, height: 200, flexShrink: 0 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData.length ? pieData : [{ name: "No data", value: 1, color: "#241a3d" }]}
                dataKey="value" nameKey="name" innerRadius="55%" outerRadius="85%" paddingAngle={2}>
                {(pieData.length ? pieData : [{ color: "#241a3d" }]).map((s, i) => <Cell key={i} fill={s.color} />)}
              </Pie>
              {pieData.length > 0 && <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} />}
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {USAGE_SEGS.map((seg) => (
            <button key={seg.key} onClick={() => setOpenSeg(openSeg === seg.key ? null : seg.key)}
              data-testid={`engineer-usage-seg-${seg.key}`}
              style={{ display: "flex", alignItems: "center", width: "100%", gap: 8, padding: "7px 8px", marginBottom: 2,
                background: openSeg === seg.key ? "var(--color-bg-panel, #0f0a1e)" : "transparent",
                border: "none", borderRadius: 8, cursor: "pointer", textAlign: "left" }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: seg.color, flexShrink: 0 }} />
              <span style={{ fontSize: 13, color: "var(--color-text-2)", flex: 1 }}>{seg.label}</span>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700, color: "var(--color-text-1)" }}>
                {counts[seg.key] ?? "—"}
              </span>
            </button>
          ))}
          <div style={{ fontSize: 11.5, color: MUTE, marginTop: 6 }}>
            "Neither" measured against {d?.rosterSize?.toLocaleString() || "—"} onboarded engineers
            {d?.rosterSource === "workos" ? " (WorkOS)" : ""} · click a row for names
          </div>
        </div>
      </div>
      {openSeg && d?.names && (
        <div style={{ marginTop: 10, maxHeight: 140, overflowY: "auto", display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="engineer-usage-names">
          {(d.names[openSeg] || []).length === 0 && <span style={{ fontSize: 12.5, color: MUTE }}>No engineers in this segment.</span>}
          {(d.names[openSeg] || []).map((n) => (
            <span key={n} style={{ fontSize: 12, color: "var(--color-text-2)", background: "var(--color-bg-panel, #0f0a1e)",
              border: "1px solid var(--hairline)", borderRadius: 999, padding: "4px 10px" }}>{n}</span>
          ))}
        </div>
      )}
      {needsReupload && (
        <div style={{ fontSize: 12, color: AMBER, marginTop: 10 }}>
          Weekly LPO usage needs a fresh PO Requests upload — stored LPO data predates weekly tracking.
        </div>
      )}
    </Card>
  );
}


function Transfer({ month }) {
  const [data, setData] = useState(null);      // { series, latestMonth, hasLpo, lastUploaded, totals, ... }
  const [users, setUsers] = useState(null);    // { leaderboard: [{name,count,lpoOrders}], hasLpo }
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [timelineUser, setTimelineUser] = useState(null); // engineer name for the timeline modal
  const fileRef = useRef(null);

  const loadTransfer = useCallback(async () => {
    const d = await apiGet("/supply-scanner/transfer");
    setData(d || { series: [], hasLpo: false });
    return d;
  }, []);

  const loadUsers = useCallback(async (m) => {
    const d = await apiGet("/supply-scanner/users", { params: m ? { month: m } : {} });
    setUsers(d || null);
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { await loadTransfer(); }
      finally { setLoading(false); }
    })();
  }, [loadTransfer]);

  useEffect(() => { loadUsers(month || null); }, [month, loadUsers]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await apiPost("/supply-scanner/lpo/upload", fd, { timeout: 120000 });
      toast.success(`LPO data updated — ${res.rowsParsed?.toLocaleString()} rows across ${res.months?.length} month(s).`);
      await loadTransfer();
      await loadUsers(month || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed — check it's the PO Requests CSV export.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (loading) return <Card><div style={{ color: MUTE, padding: 24 }}>Loading transfer metrics…</div></Card>;

  const series = data?.series || [];
  // Few months of history (e.g. right after the Aug-31 floor) → show labelled
  // dots so a single month still reads, rather than an empty line.
  const sparse = series.length > 0 && series.length <= 6;
  const totals = data?.totals || null;
  // When "All time" is picked build a synthetic aggregate row so the KPI card
  // still renders — cumulative through today, from backend union counts.
  const aggregateRow = totals ? {
    label: "All time",
    ssOrders: totals.ssOrders,
    lpoOrders: totals.lpoOrders,
    ssUsers: totals.ssUsers,
    lpoUsers: totals.lpoUsers,
    transferOrders: totals.transferOrders,
    transferUsers: totals.transferUsers,
  } : null;
  const row = month ? (series.find((s) => s.month === month) || null) : aggregateRow;
  const hasLpo = data?.hasLpo;

  const uploadBox = (
    <Card style={{ marginBottom: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700, color: "var(--color-text-1)", fontSize: 15, marginBottom: 4 }}>
            LPO data {hasLpo ? "" : "— not uploaded yet"}
          </div>
          <div style={{ fontSize: 12.5, color: MUTE }}>
            {data?.lastUploaded
              ? <>Last uploaded {new Date(data.lastUploaded).toLocaleString("en-GB")}{data.lpoFilename ? ` · ${data.lpoFilename}` : ""} · {data.lpoMonthsCovered?.length || 0} months covered</>
              : "Upload the PO Requests CSV export (File → Download → CSV in Google Sheets)."}
          </div>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onUpload} style={{ display: "none" }} data-testid="lpo-upload-input" />
          <Button className="btn btn-primary" disabled={uploading} onClick={() => fileRef.current?.click()}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }} data-testid="lpo-upload-btn">
            {uploading ? <><Loader2 size={15} className="animate-spin" /> Uploading…</> : <><Upload size={15} /> Upload PO Requests CSV</>}
          </Button>
        </div>
      </div>
    </Card>
  );

  if (!hasLpo) {
    return (
      <>
        {uploadBox}
        <Card>
          <div style={{ color: MUTE, fontSize: 14, padding: 8 }}>
            No LPO data yet. Upload the <strong style={{ color: "var(--color-text-1)" }}>PO Requests</strong> CSV above to see how much
            ordering has moved off LPO onto OSS. Transfer % = OSS orders ÷ (OSS + LPO).
          </div>
        </Card>
        <div style={{ marginTop: 16 }}><ReminderCard /></div>
      </>
    );
  }

  const monthLabel = row?.label || (month ? month : "All time");
  const noLpoThisMonth = month && row && row.lpoOrders === 0;

  return (
    <>
      {uploadBox}

      {noLpoThisMonth && (
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
          <span style={{ fontSize: 12, color: AMBER }} data-testid="transfer-no-lpo-note">
            No LPO rows for {monthLabel} — upload a sheet covering it.
          </span>
        </div>
      )}

      {row && (
        <KpiRow
          items={[
            { label: "Transfer — orders", value: `${row.transferOrders}%`, valueColor: pctColor(row.transferOrders),
              hint: month
                ? "OSS orders ÷ (OSS + LPO orders) this month. 100% means every supply order this month went through OSS."
                : "OSS orders ÷ (OSS + LPO orders) since your first OSS order. Older LPO history is excluded so the comparison is fair." },
            { label: "Transfer — users", value: `${row.transferUsers}%`, valueColor: pctColor(row.transferUsers),
              hint: month
                ? "OSS ordering engineers ÷ (OSS + LPO ordering engineers) this month."
                : "Distinct OSS ordering engineers ÷ distinct (OSS + LPO) ordering engineers since your first OSS order." },
            { label: "OSS orders", value: row.ssOrders?.toLocaleString(), valueColor: PURPLE,
              hint: month ? "Orders placed via OSS this month." : "Total OSS orders since your first OSS order." },
            { label: "LPO orders", value: row.lpoOrders?.toLocaleString(), valueColor: MUTE,
              hint: month
                ? "Orders placed via LPO this month."
                : "Total LPO orders since your first OSS order. Older LPO history is excluded so 'since first OSS' is a fair comparison." },
            { label: "OSS users", value: row.ssUsers, valueColor: CYAN,
              hint: month
                ? "Distinct engineers who placed at least one OSS order this month."
                : "Distinct engineers who have placed at least one OSS order since OSS started." },
            { label: "LPO users", value: row.lpoUsers?.toLocaleString(), valueColor: MUTE,
              hint: month
                ? "Distinct engineers who placed at least one LPO order this month."
                : "Distinct engineers who have placed at least one LPO order since OSS started." },
            ...(totals ? [
              { label: "OSS only", value: totals.ossOnlyUsers?.toLocaleString(), valueColor: PURPLE,
                hint: "Distinct engineers ordering on OSS but NOT LPO (since the Aug-31 floor)." },
              { label: "LPO only", value: totals.lpoOnlyUsers?.toLocaleString(), valueColor: AMBER,
                hint: "Distinct engineers ordering on LPO but never OSS." },
              { label: "Using both", value: totals.bothUsers?.toLocaleString(), valueColor: CYAN,
                hint: "Distinct engineers ordering on BOTH systems." },
            ] : []),
          ]}
        />
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <OssVsLpoCard />
        <EngineerUsageCard />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        {chartCard("Cumulative adoption curve",
          <LineChart data={series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} />
            <YAxis tick={AXIS} unit="%" domain={[0, "auto"]} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="cumTransferOrders" name="Orders on OSS" stroke={PINK} strokeWidth={2} dot={sparse ? { r: 4, fill: PINK, strokeWidth: 0 } : false}>
              {sparse && <LabelList dataKey="cumTransferOrders" position="top" fill={PINK} fontSize={11} fontWeight={700} formatter={(v) => `${v}%`} />}
            </Line>
            <Line type="monotone" dataKey="cumTransferUsers" name="Users on OSS" stroke={CYAN} strokeWidth={2} dot={sparse ? { r: 4, fill: CYAN, strokeWidth: 0 } : false}>
              {sparse && <LabelList dataKey="cumTransferUsers" position="bottom" fill={CYAN} fontSize={11} fontWeight={700} formatter={(v) => `${v}%`} />}
            </Line>
          </LineChart>, "Running share of ordering that has moved to OSS")}

        {chartCard("Monthly transfer %",
          <LineChart data={series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
            <CartesianGrid stroke="#241a3d" vertical={false} />
            <XAxis dataKey="label" tick={AXIS} />
            <YAxis tick={AXIS} unit="%" domain={[0, "auto"]} />
            <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} formatter={(v) => `${v}%`} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="transferOrders" name="Orders" stroke={PINK} strokeWidth={2} dot={sparse ? { r: 4, fill: PINK, strokeWidth: 0 } : false}>
              {sparse && <LabelList dataKey="transferOrders" position="top" fill={PINK} fontSize={11} fontWeight={700} formatter={(v) => `${v}%`} />}
            </Line>
            <Line type="monotone" dataKey="transferUsers" name="Users" stroke={CYAN} strokeWidth={2} dot={sparse ? { r: 4, fill: CYAN, strokeWidth: 0 } : false}>
              {sparse && <LabelList dataKey="transferUsers" position="bottom" fill={CYAN} fontSize={11} fontWeight={700} formatter={(v) => `${v}%`} />}
            </Line>
          </LineChart>, "Per-month share — noisier, but shows the trend")}
      </div>

      <Card style={{ marginBottom: 16 }}>
        <SectionHeading title="OSS vs LPO orders per user"
          subtitle={"\"LPO since last OSS\" = LPO placed after their most recent OSS order — anyone ≥1 has reverted"} />
        <DataTable
          perPage={12}
          searchKeys={["name"]}
          searchPlaceholder="Search engineer…"
          defaultSortKey="lpoSinceLastOss"
          defaultSortDir="desc"
          columns={[
            { key: "name", label: "Engineer", width: "1.5fr" },
            { key: "count", label: "OSS orders", align: "right", width: "0.8fr",
              render: (r) => <span style={{ color: PURPLE, fontWeight: 600 }}>{r.count}</span> },
            { key: "lpoTotal", label: "LPO total", align: "right", width: "0.8fr",
              render: (r) => <span style={{ color: r.lpoTotal > 0 ? AMBER : MUTE, fontWeight: 500 }}>{r.lpoTotal}</span> },
            { key: "lpoSinceLastOss", label: "LPO since last OSS", align: "right", width: "1fr",
              render: (r) => <span style={{ color: r.lpoSinceLastOss > 0 ? RED : GREEN, fontWeight: 700 }}>{r.lpoSinceLastOss}</span> },
            { key: "oss", label: "% on OSS", align: "right", width: "0.8fr",
              render: (r) => {
                const t = (r.count || 0) + (r.lpoTotal || 0);
                const p = t ? Math.round((r.count / t) * 100) : 0;
                return <span style={{ color: pctColor(p), fontWeight: 600 }}>{t ? `${p}%` : "—"}</span>;
              } },
            { key: "timeline", label: "Timeline", align: "right", width: "0.7fr", sortable: false,
              render: (r) => (
                <button onClick={() => setTimelineUser(r.name)} data-testid="user-timeline-btn"
                  style={{ border: "1px solid var(--hairline)", background: "transparent", color: CYAN, cursor: "pointer",
                    borderRadius: 7, padding: "3px 9px", fontSize: 11.5, fontWeight: 600 }}>View</button>
              ) },
          ]}
          rows={(users?.leaderboard || []).map((r) => ({ ...r, lpoTotal: r.lpoTotal ?? 0, lpoSinceLastOss: r.lpoSinceLastOss ?? 0 }))}
        />
        {!users?.hasLpo && <div style={{ fontSize: 12, color: AMBER, marginTop: 10 }}>LPO counts unavailable — upload the PO Requests CSV.</div>}
      </Card>

      {timelineUser && <UserTimelineModal user={timelineUser} onClose={() => setTimelineUser(null)} />}

      <RegionReverterTable />

      <ReminderCard />
    </>
  );
}

// Per-user weekly OSS-vs-LPO timeline — read whether an engineer adopted (OSS
// after LPO) or reverted (LPO after OSS). LPO is weekly-aggregated, so this is
// weekly bars, not a dot-per-order plot.
function UserTimelineModal({ user, onClose }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const r = await apiGet("/supply-scanner/user-timeline", { params: { user } }).catch(() => null);
      if (!cancelled) setD(r || { series: [] });
    })();
    return () => { cancelled = true; };
  }, [user]);

  const series = d?.series || [];
  const verdicts = {
    adopted: { t: "Adopted — OSS is their latest activity", c: GREEN },
    reverted: { t: "Reverted — back on LPO after OSS", c: RED },
    lpo_only: { t: "LPO only — never ordered on OSS", c: AMBER },
    none: { t: "No orders on record", c: MUTE },
  };
  const v = verdicts[d?.verdict] || verdicts.none;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(6px)",
      zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="user-timeline-modal">
      <div onClick={(e) => e.stopPropagation()} style={{ width: "min(760px, 95vw)", background: "rgba(15,10,30,0.92)",
        border: "1px solid rgba(96,240,248,0.2)", borderRadius: 16, padding: 22, boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--color-text-1)" }}>{user}</div>
          <button onClick={onClose} style={{ border: "1px solid var(--hairline)", background: "transparent", color: "var(--color-text-2)",
            cursor: "pointer", borderRadius: 8, padding: "4px 12px", fontSize: 12.5, fontWeight: 600 }}>Close</button>
        </div>
        <div style={{ fontSize: 12.5, color: MUTE, marginBottom: 16 }}>
          Weekly OSS vs LPO orders — <span style={{ color: v.c, fontWeight: 700 }}>{v.t}</span>
          {d && (d.ossTotal != null) && <> · {d.ossTotal} OSS / {d.lpoTotal} LPO all-time</>}
        </div>
        {!d ? <div style={{ color: MUTE, padding: 30 }}>Loading…</div>
          : series.length === 0 ? <div style={{ color: MUTE, padding: 30 }}>No orders on record for this engineer.</div>
          : (
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 6, right: 12, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke="#241a3d" vertical={false} />
                  <XAxis dataKey="label" tick={AXIS} minTickGap={20} />
                  <YAxis tick={AXIS} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={TT_ITEM} labelStyle={TT_LABEL} cursor={{ fill: "#ffffff08" }} labelFormatter={(l) => `Week commencing ${l}`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="oss" name="OSS" fill={PURPLE} radius={[3, 3, 0, 0]} />
                  <Bar dataKey="lpo" name="LPO" fill={AMBER} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        {d && d.weeklyLpoAvailable === false && (
          <div style={{ fontSize: 11.5, color: AMBER, marginTop: 8 }}>
            Weekly LPO not yet uploaded — LPO bars may be missing. Upload a fresh PO Requests export.
          </div>
        )}
      </div>
    </div>
  );
}

// Daily midday reminder to upload the PO Requests export.
function ReminderCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => setCfg(await apiGet("/settings/lpo-reminder") || { enabled: false, recipient_email: "", hour: 12 }))(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const saved = await apiPut("/settings/lpo-reminder", {
        enabled: cfg.enabled, recipient_email: cfg.recipient_email, hour: Number(cfg.hour) || 12,
      });
      setCfg(saved);
      toast.success("Reminder settings saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save reminder settings.");
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    try {
      await apiPost("/settings/lpo-reminder/test", { recipient_email: cfg.recipient_email });
      toast.success("Test reminder sent.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed — is email (Resend) configured on the server?");
    }
  };

  if (!cfg) return null;
  const inputStyle = { background: "#17102b", color: "var(--color-text-1)", border: "1px solid #2a1c47", borderRadius: 8, padding: "6px 10px", fontSize: 13 };

  return (
    <Card>
      <SectionHeading title={<span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Bell size={15} /> Daily upload reminder</span>}
        subtitle="Emails you each day to upload a fresh PO Requests export" />
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: "var(--color-text-2)" }}>
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="lpo-reminder-enabled" />
          Enabled
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: MUTE }}>
          Recipient
          <input type="email" value={cfg.recipient_email || ""} placeholder="you@octopusenergy.services"
            onChange={(e) => setCfg({ ...cfg, recipient_email: e.target.value })} style={{ ...inputStyle, width: 240 }} data-testid="lpo-reminder-email" />
        </label>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13, color: MUTE }}>
          At
          <select value={cfg.hour ?? 12} onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })} style={inputStyle} data-testid="lpo-reminder-hour">
            {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}
          </select>
          <span style={{ fontSize: 12 }}>UK time</span>
        </label>
        <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
          <Button className="btn btn-secondary" onClick={sendTest} disabled={saving}>Send test</Button>
          <Button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

// Shared with the TV/kiosk view (SupplyScannerTv.js).
export { ChoroplethMap, STATUS_COLORS, SUPPLIER_COLORS, tooltipStyle, TT_ITEM, TT_LABEL, PINK, PURPLE, CYAN, GREEN, AMBER, RED, MUTE };
